import React from 'react';
import update from 'react-addons-update';
import { v2, dirnorm } from './v2.mjs';
import { geomContains, createObstacle } from './geom.mjs';
import { presets as presets0, presets_base, presets_variants } from './presets_mission.mjs'
import { Rng } from './rand';
import { SimView } from './SimView';
import { ENTITY_CONFIG_TMPL } from './opts.mjs';
import {
  team0_tmpl_agent_ar_high,
} from './presets.mjs';
import * as extobj from './extobj';

import './Editor.css';

import { svg_icon_target, svg_icon_neutralized, svg_icon_new_target, svg_icon_updated_target } from './FigmaIngameView.js';
import { PortraitWrapper } from './PortraitsView';
import { triggerSound } from './sound.mjs';
import {
  createEnemyPreset,
  enemy_category,
  getAllEnemies,
} from './presets_enemies.mjs';

import {
  replaceTierFirearm,
} from './presets_firearm.mjs';

const OBSTACLE_TY = ['half', 'full', 'fence', 'door', 'random'];

function layerEq(a, b) {
  a = a ?? 'base';
  b = b ?? 'base';
  return a === b;
}

const LAYERS_DEFAULT = [
  { name: 'base', visible: true },
];

const presets = { ...presets0 };
for (const key of Object.keys(presets_variants)) {
  delete presets[key];
}
for (const [key, value] of Object.entries(presets_base)) {
  presets[key] = value;
}

const EDITOR_KEYS = Object.keys(presets).filter((key) => presets[key]().editor);

function boundingBox(boxes) {
  let tl = new v2(Infinity, Infinity);
  let br = new v2(-Infinity, -Infinity);
  if (boxes.length === 0) {
    return { tl: new v2(0, 0), br: new v2(0, 0) };
  }
  for (const box of boxes) {
    const { pos, extent, heading } = box;
    const p1 = pos.add(extent.rot(v2.zero(), heading));
    const p2 = pos.add(new v2(extent.x, -extent.y).rot(v2.zero(), heading));
    const p3 = pos.add(new v2(-extent.x, -extent.y).rot(v2.zero(), heading));
    const p4 = pos.add(new v2(-extent.x, extent.y).rot(v2.zero(), heading));
    const local = v2.polygonAABB([p1, p2, p3, p4]);
    tl = tl.min(local.tl);
    br = br.max(local.br);
  }
  return { tl, br };
}

export class DivCanvas extends React.Component {
  render() {
    const { spawnareas, layerName, routeLayerName, controls_list, features, segments, enemies, prev_week_enemies, enemies_diff, current, scrollRefs, isHistory, segments_report, agentsAll } = this.props;
    const { setCurrent } = this.props;
    const obstacle_specs = this.props.obstacle_specs.filter((o) => o.layer === layerName);

    const AABB = boundingBox([...spawnareas, ...obstacle_specs]);
    const compactWidth = AABB.br.x - AABB.tl.x;
    const compactHeight = AABB.br.y - AABB.tl.y;
    const fulls = [];
    for (let i = 0; i < obstacle_specs.length; i++) {
      const o = obstacle_specs[i];
      if (!['full', 'door'].includes(o.ty)) {
        continue;
      }

      const { pos, extent } = o;
      const heading = dirnorm(o.heading);
      const style = {
        left: pos.x - extent.x - AABB.tl.x,
        top: pos.y - extent.y - AABB.tl.y,
        width: extent.x * 2,
        height: extent.y * 2,
        transform: `rotate(-${heading}rad)`,
        zIndex: 2,
      };
      const cls = `plancanvas-block ${o.ty}`;

      // shadow with color: ##1b1109
      const shadowstyle = {
        left: pos.x - extent.x - AABB.tl.x - 2,
        top: pos.y - extent.y - AABB.tl.y - 2,
        width: extent.x * 2 + 4,
        height: extent.y * 2 + 4,
        transform: `rotate(-${heading}rad)`,
        zIndex: 1,
      };
      fulls.push(<div key={`full-${i}`} className={cls} style={style} />);
      fulls.push(<div key={`full-shadow-${i}`} className="plancanvas-block shadow" style={shadowstyle} />);
    }

    const rootstyle = {
      width: compactWidth,
      height: compactHeight,
    };

    const route_points = this.props.obstacle_specs
      .filter((o) => o.layer === routeLayerName)
      .map((o) => ({ order: +o.doorname?.split('-')[1], ...o }))
      .sort((a, b) => a.order - b.order);

    const routeWidth = 20;
    const routes = [];
    if (route_points.length > 1) {
      if (route_points.length > 2) {
        for (let i = 1; i < route_points.length - 1; i++) {
          const { pos } = route_points[i];
          const pointStyle = {
            left: pos.x - AABB.tl.x - routeWidth / 2,
            top: pos.y - AABB.tl.y - routeWidth / 2,
            width: routeWidth,
            height: routeWidth,
          };
          routes.push(<div key={`route-${i}-point`} className={"plancanvas-route-point"} style={pointStyle} />);
        }
        for (let i = 0; i < route_points.length - 1; i++) {
          const { pos: startPos } = route_points[i];
          const { pos: endPos } = route_points[i + 1];
          const center = startPos.add(endPos).mul(0.5);
          const length = endPos.sub(startPos).len();
          const style = {
            left: center.x - length / 2 - AABB.tl.x,
            top: center.y - routeWidth / 2 - AABB.tl.y,
            width: length,
            height: routeWidth,
            transform: `rotate(${-(Math.PI / 2 - endPos.sub(startPos).dir())}rad)`,
          };
          routes.push(<div key={`route-${i}`} className={"plancanvas-route route-" + i} style={style} />);
        }
        const { pos } = route_points[route_points.length - 1];
        const goalStyle = {
          left: pos.x - AABB.tl.x - routeWidth,
          top: pos.y - AABB.tl.y - routeWidth,
          width: routeWidth * 2,
          height: routeWidth * 2,
        };
        routes.push(<div key={`route-${route_points.length - 1}-goal`} className={"plancanvas-route-goal"} style={goalStyle} />);
      }
    } else {

    }

    const overlayBackgrounds = spawnareas.filter((o) => o.layer === layerName).map((o, i) => {
      const { pos, extent, heading } = o;
      const style = {
        left: pos.x - extent.x - AABB.tl.x,
        top: pos.y - extent.y - AABB.tl.y,
        width: extent.x * 2,
        height: extent.y * 2,
        transform: `rotate(-${heading}rad)`,
      };

      return <FigmaPlanAreaBackgroundView
        key={`figmaplan-area-overlay-background-${i}`}
        spawnarea={o}
        style={style}
        isCurrent={i === current}
        onClick={() => {
          if (isHistory) {
            return;
          }
          triggerSound('UI_Outgame_Button_Click_Default');
          setCurrent(i);
          if (scrollRefs) {
            scrollRefs[i].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center', });
          }
        }}
      />;
    });

    const overlays = spawnareas.filter((o) => o.layer === layerName).map((o, i) => {
      const { pos, extent, heading } = o;
      const style = {
        left: pos.x - extent.x - AABB.tl.x,
        top: pos.y - extent.y - AABB.tl.y,
        width: extent.x * 2,
        height: extent.y * 2,
        transform: `rotate(-${heading}rad)`,
      };

      let count = 0;
      let killed = 0;
      let newCount = 0;
      let updatedCount = 0;
      const deaths = [];
      const icons = [];
      const controls = controls_list?.[i];
      if (controls) {
        const doors = segments[i].doors;
        controls.firepolicy === 'default' && icons.push({ key: 'default' });
        controls.firepolicy === 'focus' && icons.push({ key: 'focus' });
        controls.firepolicy === 'aggressive' && icons.push({ key: 'aggressive', ty: 'firepolicy' });
        controls.firepolicy === 'pair' && icons.push({ key: 'pair' });
        controls.firepolicy === 'control' && icons.push({ key: 'control' });
        if (features.throwable) {
          !controls.throwopts && icons.push({ key: 'forbid', ty: 'throwopts' });
          controls.throwopts && icons.push({ key: 'allow' });
        }
        if (features.heal) {
          !controls.healopts && icons.push({ key: 'forbid', ty: 'healopts' });
          controls.healopts === 'conservative' && icons.push({ key: 'conservative' });
          controls.healopts === 'aggressive' && icons.push({ key: 'aggressive' });
        }
        if (doors.length > 0 && (features.breachable_attach || features.breachable_throw)) {
          controls.breachopts === 'nobreach' && icons.push({ key: 'nobreach' });
          controls.breachopts === 'breach_attach' && icons.push({ key: 'breach_attach' });
          controls.breachopts === 'breach_throw' && icons.push({ key: 'breach_throw' });
        }
        controls.riskdir && icons.push({ key: 'riskdir' });
        controls.door_entry && icons.push({ key: 'door_entry' });
        controls.throwable_cooking && icons.push({ key: 'throwable_cooking' });
      }

      if (enemies) {
        count = enemies.filter((e) => segments[i].spawnareas.includes(e.spawnarea)).length;
      }
      if (prev_week_enemies) {
        newCount = count - prev_week_enemies.filter((e) => segments[i].spawnareas.includes(e.spawnarea)).length;
      }
      if (enemies_diff) {
        newCount = enemies_diff.added.filter(({ enemy_idx }) => segments[i].spawnareas.includes(enemies[enemy_idx].spawnarea)).length;
        updatedCount = enemies_diff.updated.filter(({ enemy_idx }) => segments[i].spawnareas.includes(enemies[enemy_idx].spawnarea)).length;
      }

      if (segments_report) {
        const current_report = segments_report[i];
        killed = current_report.killed;
        if (i === 0) {
          Object.keys(current_report.allies_detail).filter((k) => current_report.allies_detail[k].life <= 0).forEach((k) => {
            deaths.push(+k);
          });
        } else {
          const prev_report = segments_report[i - 1];
          Object.keys(current_report.allies_detail).filter((k) => prev_report.allies_detail[k].life > 0 && current_report.allies_detail[k].life <= 0).forEach((k) => {
            deaths.push(+k);
          });
        }
      }
      const cls = `plancanvas-block segment`;
      return <FigmaPlanAreaOverlayView key={`segment-${i}`}
        segment={i}
        style={style}
        icons={icons}
        targetCnt={count - killed - newCount - updatedCount}
        neutralizedCnt={killed}
        newTargetCnt={newCount}
        updatedTargetCnt={updatedCount}
        onClick={() => {
          if (isHistory) {
            return;
          }
          triggerSound('UI_Outgame_Button_Click_Default');
          setCurrent(i);
          if (scrollRefs) {
            scrollRefs[i].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center', });
          }
        }}
        isCurrent={i === current}
        scrollRefs={scrollRefs}
        deaths={deaths}
        agentsAll={agentsAll}
      />;
    });

    return <div className="plancanvas" style={rootstyle}>
      {fulls}
      {overlayBackgrounds}
      {routes}
      {overlays}
    </div >;
  }
}

function FigmaPlanAreaBackgroundView(props) {
  const { spawnarea, style, isCurrent, onClick } = props;
  return <div
    className={'figmaplan-area-background' + (isCurrent ? ' current' : '')}
    style={style}
    onClick={onClick}>
  </div>
}

function FigmaPlanAreaOverlayView(props) {
  const { segment, style, icons, targetCnt, neutralizedCnt, newTargetCnt, updatedTargetCnt, isCurrent, scrollRefs, deaths, agentsAll } = props;
  const { onClick } = props;

  return <div
    className={'figmaplan-area-overlay' + (isCurrent ? ' current' : '')}
    style={style}
    onClick={onClick}
    ref={(e) => {
      if (scrollRefs) {
        scrollRefs[segment] = e;
      }
    }}>
    <div className='figmaplan-area-overlay-info'>
      <div className='figmaplan-area-overlay-segment'>{(segment + 1).toLocaleString('en-US', { minimumIntegerDigits: 2 })}</div>
      <div className='figmaplan-area-overlay-icons'>{icons.map((icon, i) => <SvgPolicyIcon key={i} svgKey={icon.key} ty={icon.ty} sizeOverride={23} />)}</div>
    </div>
    <div className='figmaplan-area-overlay-hall-of-shame'>
      {deaths.map((k, i) => {
        const agent = agentsAll.find((a) => a.idx === k);
        return <div key={i} className='figmaplan-area-overlay-hall-of-shame-agent'>
          <PortraitWrapper agent={agent} className="portrait-frame-small5" noRole={true} />
        </div>;
      })}
    </div>
    <div className='figmaplan-area-overlay-tallymarks'>
      {new Array(newTargetCnt).fill(0).map((_, i) => {
        return <div key={i} className='figmaingame-tallymark-target'>{svg_icon_new_target}</div>
      })}
      {new Array(updatedTargetCnt).fill(0).map((_, i) => {
        return <div key={i} className='figmaingame-tallymark-target'>{svg_icon_updated_target}</div>
      })}
      {new Array(targetCnt).fill(0).map((_, i) => {
        return <div key={i} className='figmaingame-tallymark-target'>{svg_icon_target}</div>
      })}
      {new Array(neutralizedCnt).fill(0).map((_, i) => {
        return <div key={i} className='figmaingame-tallymark-neutralized'>{svg_icon_neutralized}</div>
      })}
    </div>
  </div>;
}

class Canv extends React.Component {
  constructor(props) {
    super(props);

    this.canvasRef = React.createRef();
    this.configRef = React.createRef();
    this.textRef = React.createRef();

    this.searchTextRef = React.createRef();

    this.state = this.initialState(props);

    this.mouseDown = this.onMouseDown.bind(this);
    this.mouseMove = this.onMouseMove.bind(this);
    this.mouseUp = this.onMouseUp.bind(this);
    this.wheel = this.onWheel.bind(this);
    this.keyBindDown = (e) => this.onKeyBind(e, false);
    this.keyBindUp = (e) => this.onKeyBind(e, true);

    this.transposeAll = this.transposeAll.bind(this);
  }

  initialState(_props) {
    return {
      world: { width: 800, height: 800 },

      transposeX: 0,
      transposeY: 0,

      layers: LAYERS_DEFAULT,
      layer_filter: {},
      curLayer: 'base',
      moveLayer: null,

      obstacle_specs: [],
      spawnareas: [],
      spawnareas_search: [],
      enemies_info: [],

      firearm_tier: 5,

      alt: false,
      moving: { obs: null },
      current: { creating: false, resizing: false, parallelResizing: false },

      optShowIndex: false,
      optEditmodePoint: true,
      optShowGrid: true,
      optSnapping: false,
    };
  }

  componentDidMount() {
    this.renderCanvas();
    document.addEventListener('mousedown', this.mouseDown);
    document.addEventListener('mousemove', this.mouseMove);
    document.addEventListener('mouseup', this.mouseUp);
    document.addEventListener('wheel', this.wheel, { passive: false });
    document.addEventListener('keydown', this.keyBindDown);
    document.addEventListener('keyup', this.keyBindUp);

    this.canvasRef.current.addEventListener('contextmenu', e => e.preventDefault());

    const edit = window.sessionStorage.getItem('edit');
    if (edit) {
      this.setState(this.deserialize(edit));
    }

    if (!window.editor) {
      window.editor = this;
    }
  }

  preview(extobjs) {
    const simstate = presets['ext']();
    extobj.convert_world(extobjs, simstate);
    const { world, obstacle_specs, spawnareas } = simstate;
    this.setState({ world, obstacle_specs, spawnareas });
  }

  sync(extobjs) {
    const { spawnareas, obstacle_specs, layers } = this.state;
    const simstate = presets['ext']();
    simstate.spawnareas = spawnareas.filter((s) => {
      let layer = layers.find((l) => layerEq(l.name, s.layer));
      return layer.visible;
    });
    extobj.convert_world(extobjs, simstate);

    for (let i = 0; i < obstacle_specs.length; i++) {
      const prev = obstacle_specs[i];
      let layer = layers.find((l) => layerEq(l.name, prev.layer));
      if (!layer.visible) {
        continue;
      }
      const next = simstate.obstacle_specs.find((o) => o.tags.includes(`${i}`));
      if (!next) {
        continue;
      }
      prev.pos = next.pos;
      prev.extent = next.extent;
      prev.heading = next.heading;
    }
    this.setState({ spawnareas, obstacle_specs });
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.mouseDown);
    document.removeEventListener('mousemove', this.mouseMove);
    document.removeEventListener('mouseup', this.mouseUp);
    document.removeEventListener('wheel', this.wheel);
    document.removeEventListener('keydown', this.keyBindDown);
    document.removeEventListener('keyup', this.keyBindUp);

    if (this === window.editor) {
      delete window.editor;
    }
  }

  deserialize(text) {
    const { world, layers, obstacle_specs, spawnareas, enemies_info, firearm_tier } = JSON.parse(text);
    return {
      world: world ? { width: world.width, height: world.height } : { width: 800, height: 800 },
      layers: layers ?? LAYERS_DEFAULT,
      obstacle_specs: this.loadConfig(obstacle_specs),
      spawnareas: this.loadConfig(spawnareas) ?? [],
      enemies_info: enemies_info ?? [],
      firearm_tier: firearm_tier ?? 5,
    };
  }

  serialize() {
    const { world, layers, spawnareas, obstacle_specs, enemies_info, firearm_tier, m } = this.state;

    // enemies_info에 vis_group === 0이 있다면 모든 vis_group에 +1을 함.
    const has_visgroup0 = enemies_info.find((area) => area.find((e) => e.prop && e.prop.vis_group === 0));
    if (has_visgroup0) {
      for (const area of enemies_info) {
        for (const enemy of area) {
          if (enemy.prop && enemy.prop.vis_group !== undefined) {
            enemy.prop.vis_group++;
          }
        }
      }
    }

    const entities = [];

    return JSON.stringify({
      world,
      layers,
      spawnareas: spawnareas.map((o0) => {
        const o = { ...o0 };
        delete o.idx;
        for (const key of Object.keys(o)) {
          if (o[key] === null) {
            delete (o[key]);
          }
        }

        return o;
      }),
      obstacle_specs: obstacle_specs.map((o0) => {
        const o = { ...o0 };
        delete o.idx;
        if (!o.layer) {
          delete o.layer;
        }
        delete o.resizing;
        return o;
      }),
      firearm_tier,
      enemies_info,
      entities,
    }, null);
  }

  componentDidUpdate() {
    this.renderCanvas();

    const edit = this.serialize();
    this.textRef.current.value = edit;

    window.sessionStorage.setItem('edit', edit);
    this.serializeConfig();
  }

  update(obs, idx, command) {
    let obstacle_specs = this.state.obstacle_specs.slice();
    let spawnareas = this.state.spawnareas.slice();
    if (!OBSTACLE_TY.includes(obs.ty)) {
      if (command === "push") {
        spawnareas = update(spawnareas, {
          $push: [{ ...obs }],
        })
      } else if (command === "splice") {
        spawnareas = update(spawnareas, {
          $splice: [[idx, 1]],
        })
      } else if (command === "set") {
        spawnareas = update(spawnareas, {
          [idx]: { $set: obs }
        })
      }
    } else {
      if (command === "push") {
        obstacle_specs = update(obstacle_specs, {
          $push: [{ ...obs }],
        })
      } else if (command === "splice") {
        obstacle_specs = update(obstacle_specs, {
          $splice: [[idx, 1]],
        })
      } else if (command === "set") {
        obstacle_specs = update(obstacle_specs, {
          [idx]: { $set: obs }
        })
      }
    }
    return { obstacle_specs, spawnareas }
  }

  onMouseDown(e) {
    const { moveLayer } = this.state;
    const cursor = this.canvasCursor(e);
    if (!cursor) {
      return;
    }

    if (e.ctrlKey) {
      // TODO: duplicate obstacle
      let { idx, obs } = this.findObstacle(cursor);
      if (!obs) {
        return;
      }
      obs = { ...obs };
      if (moveLayer) {
        obs.layer = moveLayer;
      }

      // duplicate
      const obstacles = this.update(obs, idx, "push");

      this.setState({ ...obstacles }, () => {
        this.onMoveObstacle(e);
      });
      return;
    }

    if (e.altKey || e.shiftKey) {
      const { idx, obs } = this.findObstacle(cursor);
      if (!obs) {
        return;
      }

      if (e.button === 2) {
        // right click
        const obs0 = { ...obs };
        delete obs0.spawnheading;

        const obstacles = this.update(obs0, idx, "set");
        this.setState({ ...obstacles });
      } else {
        const { pos, heading, extent } = obs;
        // anchor를 찾습니다. 현재 cursor 위치에서 가장 먼 점이 anchor가 됩니다.
        const cursor2 = cursor.rot(pos, -heading).round();
        const delta = pos.sub(cursor2).sign().hadamard(extent.abs()).rot(v2.zero(), heading);
        const anchor = pos.add(delta);

        const anchor2 = pos.sub(delta);

        this.setState({
          current: {
            ...obs,
            anchor,
            anchor2,
            resizing: e.altKey,
            parallelResizing: !e.altKey,
            idx,
          },
        });
      }
      return;
    }

    this.onMoveObstacle(e);
  }

  onMoveObstacle(e) {
    const { curLayer, moveLayer, optSnapping, spawnareas } = this.state;
    const cursor = this.canvasCursor(e);
    if (!cursor) {
      return;
    }

    const { idx, obs } = this.findObstacle(cursor);

    if (obs) {
      if (e.button === 2) {
        // right click
        if (moveLayer) {
          const obstacles = this.update({ ...obs, layer: moveLayer }, idx, "set");
          this.setState({ ...obstacles });
        } else {
          const obstacles = this.update(obs, idx, "splice");
          this.setState({ ...obstacles });
        }

        // update spawnareas
        if (obs.ty === 'spawnarea') {
          const enemies_info = this.state.enemies_info.slice();
          enemies_info.splice(spawnareas.indexOf(obs), 1);
          this.setState({ enemies_info });
        }
        return;
      } else if (e.button === 1) {
        this.changeType(cursor);
      } else {
        this.setState({ moving: { obs, offset: cursor, obsoffset: obs.pos } });
      }
      return;
    }
    this.setState({
      current: {
        creating: true,
        anchor: optSnapping ? cursor.snap(5) : cursor,
        pos: optSnapping ? cursor.snap(5) : cursor,
        extent: v2.zero(),
        heading: 0,
        accHeading: 0,
        ty: 'half',
        idx,
        layer: curLayer !== 'base' ? curLayer : null,
      },
    });
  }

  changeType(cursor) {
    const { idx, obs } = this.findObstacle(cursor);
    if (!obs) {
      return;
    }

    if (obs.ty === 'half') {
      const obstacles = this.update({ ...obs, ty: 'full' }, idx, "set");
      this.setState({ ...obstacles });
    } else if (obs.ty === 'full') {
      const obstacles = this.update({ ...obs, ty: 'fence' }, idx, "set");
      this.setState({ ...obstacles });
    } else if (obs.ty === 'fence') {
      const obstacles = this.update({ ...obs, ty: 'door' }, idx, "set");
      this.setState({ ...obstacles });
    } else if (obs.ty === 'door') {
      const obs0 = { ...obs, ty: 'random', random: { ty: 'mixed', count: 0 } };
      const obstacles = this.update(obs0, idx, "set");
      this.setState({ ...obstacles });
    } else if (obs.ty === 'random') {
      const obstacles = this.update(obs, idx, "splice");
      obs.ty = 'spawnarea';
      delete obs.random;
      obstacles.spawnareas.push(obs);
      this.setState({ ...obstacles });
    } else if (obs.ty === 'spawnarea') {
      const obstacles = this.update(obs, idx, "splice");
      obs.ty = "half";
      delete obs.effect;
      delete obs.triggers;
      delete obs.spawn;
      delete obs.spawn1;
      delete obs.object;
      obstacles.obstacle_specs.push({ ...obs });
      this.setState({ ...obstacles });
    }
  }

  toggleWip(cursor) {
    const { idx, obs } = this.findObstacle(cursor);
    if (!obs) {
      return;
    }

    const obstacles = this.update({ ...obs, wip: !obs.wip }, idx, "set");
    this.setState({ ...obstacles });
  }

  putEffectType(cursor) {
    const { idx, obs } = this.findObstacle(cursor);
    if (!obs || obs.ty !== 'spawnarea') {
      return;
    }

    const effect_tys = [null, 'limvis', 'stun_gr'];
    const tyidx = effect_tys.indexOf(obs.effect_ty);
    this.setState({
      spawnareas: update(this.state.spawnareas, {
        [idx]: { effect_ty: { $set: obs.effect_ty ? effect_tys[(tyidx + 1) % effect_tys.length] : effect_tys[1] } },
      }),
    });
  }

  //chae 일단 기본적인거만 넣어놓고 대충하면 되지 않을까???!!
  putTriggers(cursor) {
    const { idx, obs } = this.findObstacle(cursor);
    if (!obs || obs.ty !== 'spawnarea') {
      return;
    }
    this.setState({
      spawnareas: update(this.state.spawnareas, {
        [idx]: {
          triggers: {
            $set: !obs.triggers ? [{
              condition: 'enter',
              conditiontarget: { ty: 'entity', team: 0 },
              action: 'push_rule',
              actiontarget: { ty: 'entity', team: 0 },
              actionrule: { ty: 'capture' }
            }] : null
          },
        }
      }),
    });
  }

  putTeam0Spawn(cursor) {
    const { idx, obs } = this.findObstacle(cursor);
    if (!obs || obs.ty !== 'spawnarea') {
      return;
    }

    const area = this.state.spawnareas[idx];

    const op = {
      spawn: { $set: null },
      spawn1: { $set: null },
      object: { $set: null },
    };

    if (area.spawn) {
      op.spawn1 = { $set: true };
    } else if (area.spawn1) {
      op.object = { $set: true };
    } else if (area.object) {
    } else {
      op.spawn = { $set: true };
    }

    this.setState({ spawnareas: update(this.state.spawnareas, { [idx]: op }) });
  }

  onMouseMove(e) {
    const cursor = this.canvasCursor(e);
    const { optSnapping } = this.state;
    if (!cursor) {
      return;
    }
    const { moving, current } = this.state;

    if (moving.obs) {
      let obs = moving.obs;
      let cursordelta = moving.offset.sub(cursor);
      obs.pos = moving.obsoffset.sub(cursordelta);
      if (optSnapping) {
        obs.pos = obs.pos.snap(5);
      }
      const obstacles = this.update(obs, moving.idx, "set");
      this.setState({ ...obstacles });
      return;
    }

    if (current.creating || current.resizing || current.parallelResizing) {
      const obs0 = { ...current };
      if (this.state.optEditmodePoint) {
        let cursor2;

        if (current.parallelResizing) {
          let parallelCursor = new v2(0, 0);

          const diff = cursor.sub(current.anchor2);

          const diff2 = diff.rot(v2.zero(), -current.heading);
          if (Math.abs(diff2.x) > Math.abs(diff2.y)) {
            parallelCursor = cursor.sub(
              new v2(0, diff2.y).rot(v2.zero(), current.heading)
            );
          } else {
            parallelCursor = cursor.sub(
              new v2(diff2.x, 0).rot(v2.zero(), current.heading)
            );
          }

          // 물체 좌표계에서 평행이동만 남긴 커서 위치
          cursor2 = parallelCursor
            .rot(current.anchor, -current.heading)
            .round();
        }
        else {
          // 물체 좌표계에서의 커서 위치
          cursor2 = cursor.rot(current.anchor, -current.heading).round();
        }

        // extent: 물체 좌표계에서 계산합니다.
        obs0.extent = cursor2.sub(current.anchor).mul(0.5).abs();

        if (optSnapping) {
          obs0.extent = obs0.extent.snap(2.5);
        }

        const posDir = obs0.extent.hadamard(cursor2.sub(current.anchor).sign()).rot(v2.zero(), current.heading);
        obs0.pos = current.anchor.add(posDir);

      } else {
        const cursor2 = cursor.rot(current.pos, -current.heading).round();
        obs0.extent = cursor2.sub(current.pos).abs();
      }

      const obstacles = this.update(obs0, current.idx, "set")

      this.setState({
        ...obstacles,
        current: update(current, { $set: obs0 }),
      });
    }

    const { obs } = this.findObstacle(cursor);

    this.setState({ cursor, obsOver: obs ? obs : null });
  }

  onMouseUp(_e) {
    const { moving, current } = this.state;
    if (moving.obs) {
      this.setState({ moving: { obs: null } });
    }

    if (current.creating) {
      if (current.extent.x === 0 || current.extent.y === 0) {
        return;
      }
      delete (current.creating);
      const obstacles = this.update(current, null, "push");

      this.setState({
        ...obstacles,
        current: { creating: false },
      });
    }

    if (current.resizing) {
      this.setState({
        current: { resizing: false },
      });
    }

    if (current.parallelResizing) {
      this.setState({
        current: { parallelResizing: false },
      });
    }
  }

  onWheel(e) {
    const cursor = this.canvasCursor(e);
    if (!cursor) {
      return;
    }

    const { obs, idx } = this.findObstacle(cursor);
    if (!obs) {
      return;
    }

    e.preventDefault();

    let obs0 = null;
    if (this.state.alt && obs.ty === 'spawnarea') {
      let spawnheading = obs.spawnheading ?? 0;
      spawnheading += e.deltaY / 1000;
      obs0 = { ...obs, spawnheading };
    } else {
      const { optSnapping } = this.state;
      const accHeading = Math.round(((obs.accHeading ?? 0) + e.deltaY / 1000) * 100) / 100;
      const snapHeading = (accHeading / (1 / 12 * Math.PI)).toFixed() * (1 / 12 * Math.PI);

      obs0 = { ...obs, heading: dirnorm(optSnapping ? snapHeading : accHeading, accHeading) };
    }

    if (obs0) {
      const obstacles = this.update(obs0, idx, "set");
      this.setState({ ...obstacles });
    }
  }

  onKeyBind(e, up) {
    this.setState({
      alt: e.altKey,
    });
    if (up) {
      return;
    }

    const { cursor } = this.state;
    if (!cursor) {
      return;
    }

    // ctrl 조합키 무시하기
    if (e.ctrlKey) {
      return;
    }

    if (e.key === 'c') {
      this.changeType(cursor);
    } else if (e.key === 'w') {
      this.toggleWip(cursor);
    } else if (e.key === '1') {
      this.putEffectType(cursor);
    } else if (e.key === '2') {
      this.putTriggers(cursor);
    } else if (e.key === '3') {
      this.putTeam0Spawn(cursor);
    }
  }

  transposeAll(x, y) {
    const { obstacle_specs, spawnareas } = this.state;
    this.setState({
      obstacle_specs: obstacle_specs.map(obs => {
        obs.pos = obs.pos.add(new v2(x, y));
        return obs;
      }),
      spawnareas: spawnareas.map(obs => {
        obs.pos = obs.pos.add(new v2(x, y));
        return obs;
      }),
    });
  }

  findObstacle(cursor, layer) {
    const { obstacle_specs, spawnareas, curLayer } = this.state;
    layer = layer ?? curLayer;

    for (let i = spawnareas.length - 1; i >= 0; i--) {
      const obs = spawnareas[i];
      if (layer && !layerEq(obs.layer, layer)) {
        continue;
      }
      const p = createObstacle(obs.pos, obs.extent, obs.heading, null, 0);
      if (geomContains(cursor, p.polygon)) {
        // return last object
        return { idx: i, obs };
      }
    }

    for (let i = obstacle_specs.length - 1; i >= 0; i--) {
      const obs = obstacle_specs[i];
      if (layer && !layerEq(obs.layer, layer)) {
        continue;
      }
      const p = createObstacle(obs.pos, obs.extent, obs.heading, null, 0);
      if (geomContains(cursor, p.polygon)) {
        return { idx: i, obs };
      }
    }

    return { idx: null, obs: null };
  }

  canvasCursor(e) {
    const { width, height } = this.state.world;

    const canvas = this.canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    let x = e.clientX - rect.left;
    let y = e.clientY - rect.top;

    if (x < 0 || y < 0 || x > width || y > height) {
      return null;
    }
    x -= width / 2;
    y -= height / 2;

    return new v2(x, y).round();
  }

  serializeConfig() {
    let spawnareas = "spawnareas: [\n";
    let obstacles = "obstacle_specs: [\n";

    const sv2 = (v) => `new v2(${v.x}, ${v.y})`;

    for (const obs of this.state.obstacle_specs) {
      obstacles += `{ pos: ${sv2(obs.pos)}, extent: ${sv2(obs.extent)}, heading: ${obs.heading}, ty: "${obs.ty}", `
        + (obs.random ? `random:` + JSON.stringify(obs.random) : ``)
        + `},\n`;
    }
    for (const area of this.state.spawnareas) {
      spawnareas += `{ pos: ${sv2(area.pos)}, extent: ${sv2(area.extent)}, heading: ${area.heading}, `;
      if (area.effect_ty) {
        spawnareas += `effect_ty: '${area.effect_ty}',`;
      }

      if (area.triggers) {
        spawnareas += `\ntriggers: `;
        spawnareas += JSON.stringify(area.triggers);
        spawnareas += `,\n`;
      }
      if (area.spawn) {
        spawnareas += `spawn: true,`;
      }
      if (area.spawn1) {
        spawnareas += `spawn1: true,`;
      }
      if (area.object) {
        spawnareas += `object: true,`;
      }
      spawnareas += `},\n`;
    }

    const s = `
${spawnareas}],
${obstacles}],`;
    this.configRef.current.value = s;
  }

  loadConfig(text) {
    for (const item of text) {
      item.pos = new v2(item.pos.x, item.pos.y);
      item.extent = new v2(item.extent.x, item.extent.y);
    }

    return text;
  }

  onTriggerOver(e) {
    this.setState({ triggerOver: e });
  }

  selectTrigger() {
    const { spawnareas, obsOver } = this.state;
    let actionruleForm = [];
    for (const area of spawnareas) {
      if (area.triggers) {
        const idx = spawnareas.indexOf(area);
        actionruleForm.push(
          <div key={idx}>
            spawnarea #{idx} trigger
            <p></p>
            <textarea
              style={{ backgroundColor: (area === obsOver ? 'pink' : '') }}
              onMouseOver={() => this.onTriggerOver(area)}
              onMouseLeave={() => this.onTriggerOver(null)}
              id={idx + "triggers"}
            >{JSON.stringify(area.triggers)}</textarea>
            <button onClick={() => {
              const t = document.getElementById(idx + "triggers").value;
              this.setState({
                spawnareas: update(spawnareas, {
                  [idx]: {
                    triggers: { $set: JSON.parse(t) },
                  }
                }),
              });
            }}>적용</button>
          </div>
        )
      }
    }
    return actionruleForm;
  }

  onRandomOver(e) {
    this.setState({
      randomOver: e
    })
  }

  selectRandomObs() {
    const { obstacle_specs, obsOver } = this.state;
    let randomObsForm = [];
    let areas = -1;
    for (const obs of obstacle_specs) {
      if (obs.ty === "random") {
        areas++;
        const idx = obstacle_specs.indexOf(obs);
        randomObsForm.push(
          <form style={{ backgroundColor: (obs === obsOver ? 'lightgreen' : '') }} key={areas}
            onMouseOver={() => this.onRandomOver(obs)} onMouseLeave={() => this.onRandomOver(null)}>
            randomArea #{areas}-
            <label htmlFor="ty">
              ty :
            </label>
            <select id="ty" onChange={(e) => {
              const randomTy = e.target.value;

              this.setState({
                obstacle_specs: update(obstacle_specs, {
                  [idx]: {
                    random: { ty: { $set: randomTy }, }
                  }
                }),
              });
            }}>
              <option value="mixed">mixed</option>
              <option value="full">full</option>
              <option value="half">half</option>
            </select>
            count :
            <input type="number" value={obs.random.count ? obs.random.count : ''} onChange={(e) => {
              const count = e.target.value;
              this.setState({
                obstacle_specs: update(obstacle_specs, {
                  [idx]: {
                    random: { count: { $set: count === '' ? 0 : count } }
                  }
                }),
              });
            }}></input>
          </form>
        );
      }
    }
    return randomObsForm;
  }

  loadbutton() {
    return EDITOR_KEYS.map((key) => {
      return <button key={key} onClick={() => {
        const preset = presets[key]()
        const { spawnareas, layers, world, obstacle_specs } = preset;
        for (const area of spawnareas) {
          area.ty = "spawnarea";
        }
        this.setState({
          world,
          layers: layers ?? LAYERS_DEFAULT,
          spawnareas: this.loadConfig(spawnareas),
          obstacle_specs: this.loadConfig(obstacle_specs),
          spawnareas_search: [],
          enemies_info: [],
        });
      }}>{key}</button>;
    })
  }

  layerState(layer) {
    const { layers, curLayer } = this.state;
    const layer_name = layer ?? 'base';
    const found = layers.find(l => l.name === layer_name);
    const visible = found?.visible ?? false;
    const selected = layer_name === curLayer;
    return { visible, selected };
  }

  renderCanvas() {
    const { world, triggerOver, randomOver } = this.state;
    const { current, obstacle_specs, spawnareas } = this.state;
    const { optShowIndex, optShowGrid } = this.state;

    const canvas = this.canvasRef.current;
    const ctx = canvas.getContext('2d');

    ctx.font = 'bold 12px monospace';

    ctx.resetTransform();

    ctx.fillStyle = 'black';
    ctx.globalAlpha = 1;
    ctx.fillRect(0, 0, world.width, world.height);

    ctx.translate(world.width / 2, world.height / 2);

    if (optShowGrid) {
      const firstLineWidth = ctx.lineWidth;

      ctx.lineWidth = 0.4;
      ctx.strokeStyle = '#444';
      ctx.beginPath();
      for (var x = - world.width / 2; x <= world.width; x += 10) {
        ctx.moveTo(x, -world.height / 2);
        ctx.lineTo(x, world.height / 2);
      }
      for (var y = - world.height / 2; y <= world.height; y += 10) {
        ctx.moveTo(-world.width, y);
        ctx.lineTo(world.width, y);
      }
      ctx.stroke();

      ctx.lineWidth = 0.4;
      ctx.strokeStyle = '#888';
      ctx.beginPath();
      for (var x = - world.width / 2; x <= world.width; x += 100) {
        ctx.moveTo(x, -world.height / 2);
        ctx.lineTo(x, world.height / 2);
      }
      for (var y = - world.height / 2; y <= world.height; y += 100) {
        ctx.moveTo(-world.width, y);
        ctx.lineTo(world.width, y);
      }
      ctx.stroke();

      ctx.lineWidth = firstLineWidth;
    }

    function renderPoly(polygon) {
      const start = polygon[polygon.length - 1];
      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      for (const p of polygon) {
        ctx.lineTo(p.x, p.y);
      }
    }

    ctx.strokeStyle = 'red';
    if (current.creating) {
      const obs = current;
      const p = createObstacle(obs.pos, obs.extent, obs.heading, null, 0);
      renderPoly(p.polygon);
      ctx.stroke();
    }

    let areas = 0;
    let randoms = 0;
    for (let i = 0; i < obstacle_specs.length; i++) {
      const obs = obstacle_specs[i];
      const { visible, selected } = this.layerState(obs.layer);
      if (!visible) {
        continue;
      }
      ctx.globalAlpha = selected ? 1 : 0.5;

      const p = createObstacle(obs.pos, obs.extent, obs.heading, null, 0);

      let style = null;
      if (obs.wip) {
        style = 'purple';
      }
      ctx.strokeStyle = style ?? 'white';
      renderPoly(p.polygon);

      if (randomOver === obs) {
        ctx.fillStyle = style ?? 'lightgreen';
        ctx.fill();
      }
      if (obs.ty === 'full') {
        ctx.fillStyle = style ?? 'white';
        ctx.fill();
      } else if (obs.ty === 'half') {
        ctx.stroke();
      } else if (obs.ty === 'fence') {
        ctx.setLineDash([5, 5]);
        ctx.stroke();
        ctx.setLineDash([]);
      } else if (obs.ty === 'door') {
        ctx.fillStyle = style ?? 'green';
        ctx.fill();
      }

      if (obs.ty === 'door' || optShowIndex) {
        const labelpos = obs.pos.sub(obs.extent);
        ctx.fillStyle = 'lightgray';
        let text = `#${i}`;
        if (obs.layer) {
          text += `: ${JSON.stringify(obs.layer)}`;
        }
        ctx.fillText(text, labelpos.x, labelpos.y);
      }

      if (obs.ty === 'door' && obs.doorname) {
        ctx.fillStyle = 'green';
        const text = `name: ${obs.doorname}`;
        ctx.fillText(text, obs.pos.x, obs.pos.y);
      }

      if (obs.ty === 'random') {
        ctx.fillStyle = 'yellow';
        const text = `random #${randoms}(ty: ${obs.random.ty}, count: ${obs.random.count})`
        ctx.fillText(text, obs.pos.x, obs.pos.y);
        randoms++;
      }
    }

    for (let i = 0; i < spawnareas.length; i++) {
      const obs = spawnareas[i];
      const { visible, selected } = this.layerState(obs.layer);
      if (!visible) {
        continue;
      }
      ctx.globalAlpha = selected ? 1 : 0.5;

      const p = createObstacle(obs.pos, obs.extent, obs.heading, null, 0);

      ctx.strokeStyle = 'white';
      renderPoly(p.polygon);
      if (triggerOver === obs) {
        ctx.fillStyle = 'pink';
        ctx.fill();
      } else {
        ctx.stroke();
      }

      if (optShowIndex) {
        const labelpos = obs.pos.sub(obs.extent);
        ctx.fillStyle = 'lightgray';
        ctx.fillText(`#${i}`, labelpos.x, labelpos.y);
      }

      ctx.fillStyle = 'yellow';
      let text = `spawnarea ${areas}`;
      if (obs.effect_ty) {
        text += `, effect=${obs.effect_ty}`;
      }
      if (obs.triggers) {
        text += `, triggers`;
      }
      if (obs.spawn) {
        text += `, startpoint`;
        ctx.fillStyle = 'skyblue';
      }
      if (obs.spawn1) {
        text += `, enemy`;
        ctx.fillStyle = 'aqua';
      }
      if (obs.object) {
        text += `, object`;
        ctx.fillStyle = 'blue';
      }
      if (obs.rescue) {
        text += `, rescue`;
      }
      ctx.fillText(text, obs.pos.x, obs.pos.y);
      if (obs.area_desc) {
        ctx.fillText(obs.area_desc, obs.pos.x, obs.pos.y + 10);
      }

      areas += 1;

      if (obs.spawnheading) {
        let len = 20;
        ctx.strokeStyle = 'yellow';
        ctx.beginPath();
        ctx.moveTo(obs.pos.x, obs.pos.y);

        const delta = v2.fromdir(obs.spawnheading).mul(len);

        ctx.lineTo(obs.pos.x + delta.x, obs.pos.y + delta.y);
        ctx.stroke();
      }
    }
  }

  renderLayers() {
    const { layers, curLayer, moveLayer } = this.state;

    return <div className="box editor-layers">
      <div>
        <p>layers</p>
      </div>
      {layers.map((l, idx) => {
        const { visible } = l;
        const selected = l.name === curLayer;

        let movebtn = <button tabIndex="-1" onClick={() => {
          this.setState({ moveLayer: l.name });
        }}>move</button>;
        if (moveLayer === l.name) {
          movebtn = <button tabIndex="-1" onClick={() => {
            this.setState({ moveLayer: null });
          }}>cancel</button>;
        }

        let clsvis = 'editor-btn-eye';
        if (!visible) {
          clsvis += ' editor-btn-eye-off';
        }

        return <div className="box" key={idx}>
          <input onChange={(e) => {
            if (l.name === 'base' || e.target.value === 'base') {
              return;
            }

            const name0 = l.name;
            const name = e.target.value;
            let op = {
              layers: update(layers, {
                [idx]: { name: { $set: name } },
              }),
            };

            const dup = layers.filter(l => l.name === name0);
            if (dup.length === 1) {
              const obstacle_specs = this.state.obstacle_specs.map(obs => {
                let layer = obs.layer === l.name ? name : obs.layer;
                return { ...obs, layer };
              });
              const spawnareas = this.state.spawnareas.map(obs => {
                let layer = obs.layer === l.name ? name : obs.layer;
                return { ...obs, layer };
              });
              op = {
                ...op,
                obstacle_specs,
                spawnareas,
              };
            }

            this.setState(op);
          }} value={l.name} />

          <button tabIndex="-1" disabled={selected} onClick={() => {
            this.setState({ curLayer: l.name });
          }}>select</button>

          {movebtn}

          <button tabIndex="-1" onClick={() => {
            this.setState({
              layers: update(layers, {
                $splice: [[idx, 1]],
              }),
            });
          }}>␡</button>

          <button tabIndex="-1" onClick={() => {
            this.setState({
              layers: update(layers, {
                [idx]: { visible: { $set: !visible } },
              }),
            });
          }} className={clsvis}>{visible ? '👁' : '-'}</button>

          {idx > 0 ? <button tabIndex="-1" onClick={() => {
            const layers1 = layers.slice();
            const idx1 = idx - 1;
            [layers1[idx], layers1[idx1]] = [layers1[idx1], layers1[idx]];
            this.setState({ layers: layers1 });
          }}>↑</button> : null}
          {(idx < layers.length - 1) ? <button tabIndex="-1" onClick={() => {
            const layers1 = layers.slice();
            const idx1 = idx + 1;
            [layers1[idx], layers1[idx1]] = [layers1[idx1], layers1[idx]];
            this.setState({ layers: layers1 });
          }}>↓</button> : null}

        </div>;
      })}
      <button tabIndex="-1" onClick={() => {
        this.setState({
          layers: update(layers, {
            $push: [{ name: 'new', visible: true }],
          }),
        });
      }}>add</button>
    </div>;
  }

  renderSpawnareas() {
    const { spawnareas, enemies_info, spawnareas_search, layers, layer_filter } = this.state;

    while (enemies_info.length < spawnareas.length) {
      enemies_info.push([]);
    }
    while (enemies_info.length > spawnareas.length) {
      enemies_info.pop();
    }

    const renderVisgroup = (prop) => {
      if (prop.vis_group !== undefined) {
        return <span>
          <input type="number" onChange={(e) => {
            const num = Math.max(1, parseInt(e.target.value));
            prop.vis_group = num;
            this.setState({ enemies_info });
          }} value={prop.vis_group} />
          <button onClick={() => {
            delete prop.vis_group;
            this.setState({ enemies_info });
          }}>-</button>
        </span>;
      } else {
        return <span>
          <button onClick={() => {
            prop.vis_group = 1;
            this.setState({ enemies_info });
          }}>+</button>
        </span>;
      }
    };

    const renderEnemyPropertices = (property) => {
      if (!property.prop) {
        property.prop = {};
      }
      const { prop } = property;
      return <div>
        vis_group {renderVisgroup(prop)}
      </div>
    };

    const renderSummary = (area) => {
      return <div class="tab_level_1">
        <div> Summary </div>
        {enemies_info[area].map((property, idx) => {
          const { name } = property;
          return <div class="tab_level_2">
            <details>
              <summary>{name}&nbsp;&nbsp;&nbsp;
                <button onClick={() => {
                  enemies_info[area].splice(idx, 1);
                  this.setState({ enemies_info });
                }}>-</button>
              </summary>
              {renderEnemyPropertices(property)}
            </details>
          </div>
        })}
      </div>
    };

    const createEnemyProperty = (area, key) => {
      enemies_info[area].push({
        name: key,
      })
      this.setState({ enemies_info });
    };

    const renderEnemy = (area, tiers) => {
      return Object.entries(tiers).map(([key, entities]) => {
        return Object.entries(entities).map(([name, entity]) => {
          return <div class="tab_level_2">
            <span>[{key}] {name}&nbsp;&nbsp;&nbsp;</span>
            <button onClick={() => createEnemyProperty(area, name)}>+</button>
          </div>
        });
      });
    };

    const renderEnemyType = (area) => {
      return Object.entries(enemy_category).map(([key, value]) => {
        return <details>
          <summary class="tab_level_1">{key}</summary>
          {renderEnemy(area, value)}
        </details>
      });
    };

    // search options
    // 단일 숫자 혹은 범위 (a~b)로 설정 가능
    // 여러 옵션을 지정하려면 콤마로 구분하기
    // e.g) 1, 3~5, 10
    const updateSearch = (text) => {
      spawnareas_search.splice(0, spawnareas_search.length);
      if (text) {
        const words = text.split(',');
        for (const word of words) {
          const ranges = word.split('~').map((str) => {
            let ret = parseInt(str);
            if (!isNaN(ret)) {
              ret = Math.max(ret, 0);
              ret = Math.min(ret, spawnareas.length - 1);
            }
            return ret;
          });

          if (ranges.length === 1 && !isNaN(ranges[0])) {
            spawnareas_search.push(ranges[0]);
          } else {
            for (let i = 1; i < ranges.length; i++) {
              if (!isNaN(ranges[i - 1]) && !isNaN(ranges[i])) {
                for (let j = ranges[i - 1]; j <= ranges[i]; j++) {
                  spawnareas_search.push(j);
                }
              }
            }
          }
        }
      }
      if (spawnareas_search.length === 0) {
        for (let i = 0; i < spawnareas.length; i++) {
          spawnareas_search.push(i);
        }
      }
      this.setState({ spawnareas_search });
    };

    const renderFirearmTier = () => {
      let { firearm_tier } = this.state;
      return <div>
        <span>Firearm Tier </span>
        <button onClick={() => {
          firearm_tier = Math.max(1, firearm_tier - 1);
          this.setState({ firearm_tier });
        }}>-</button>
        <span>&nbsp;&nbsp;&nbsp; {firearm_tier} &nbsp;&nbsp;&nbsp;</span>
        <button onClick={() => {
          firearm_tier = Math.min(5, firearm_tier + 1);
          this.setState({ firearm_tier });
        }}>+</button>
      </div>
    };

    let area_idx = 0;

    return <div className="box editor-layers" style={{ width: 300 }}>
      <div>
        <span>Spawnarea</span>
        <input ref={this.searchTextRef} type="text"></input>
        <button onClick={() => { updateSearch(this.searchTextRef.current.value) }}> search </button>
      </div>
      {layers.map((l, idx) => {
        return <span>
          <input type="checkbox" onClick={(e) => {
            layer_filter[l.name] = e.target.checked;
            this.setState({ layer_filter });
          }} />
          <label>{l.name}</label>
        </span>;
      })}
      {renderFirearmTier()}
      {spawnareas.map((area, idx) => {
        // layer visible일때만 활성화
        if (!layers.find((l) => layerEq(l.name, area.layer)).visible) {
          return;
        }
        area_idx++;
        if (!layer_filter[area.layer ?? 'base']) {
          return;
        }
        return <div className="box" key={idx}>
          <details>

            <summary>|{area.layer ?? 'base'}| Spawnarea  {area_idx - 1}
              <input type="text" onChange={(e) => {
                const text = e.target.value;
                area.area_desc = text;
                this.setState({ spawnareas });
              }} value={area.area_desc} />
            </summary>
            {renderEnemyType(idx)}
            {renderSummary(idx)}
          </details>
        </div>;
      })}
    </div>;
  }

  render() {
    const { world, transposeX, transposeY, simstate, obstacle_specs, spawnareas } = this.state;
    const { layers, optShowIndex, optEditmodePoint, optShowGrid, optSnapping } = this.state;
    const { width, height } = world;

    const makePreset = () => {
      const team1_tmpl = {
        ...ENTITY_CONFIG_TMPL,
        team: 1,
        life: 20,
        armor: 20,
        default_rule: { ty: 'idle', alert: false },
        allow_crawl: false,
      };
      const entities = [];
      for (let i = 0; i < spawnareas.length; i++) {
        const spawnarea = spawnareas[i];
        if (spawnarea.spawn) {
          entities.push({ ...team0_tmpl_agent_ar_high, spawnarea: i });
          entities.push({ ...team0_tmpl_agent_ar_high, spawnarea: i })
        }
        if (spawnarea.spawn1) {
          entities.push({ ...team1_tmpl, spawnarea: i });
        }
      }

      this.setState({
        simstate: {
          world: {
            ...world,
            simover_rule: 'eliminate',
          },

          obstacle_specs,
          entities,
          spawnareas,
          goals: [],

          mission_rules: [
            { ty: 'capture' },
            { ty: 'explore' },
          ],
        }
      })
    }

    return (
      <div className="editor-root">
        {simstate ?
          <SimView
            m={this.props.m}
            seed={Rng.randomseed()}
            debug={true}
            noembed={true}
            onFinish={() => this.setState({ simstate: null })}
            simstate={simstate}
          /> : ``}
        <div className="box">
          <p>load presets</p>
          {this.loadbutton()}
        </div>
        {this.renderLayers()}
        {this.renderSpawnareas()}
        <div>
          <button onClick={() => {
            const payload = JSON.stringify({
              world: {
                extent: {
                  x: world.width / 2,
                  y: world.height / 2,
                },
              },
              spawnareas: spawnareas.filter((s) => {
                let layer = layers.find((l) => layerEq(l.name, s.layer));
                return layer.visible;
              }).map((s) => {
                return {
                  pos: s.pos,
                  extent: s.extent,
                  heading: s.heading,
                };
              }),
              obstacles: obstacle_specs.map((obs, idx) => {
                let layer = layers.find((l) => layerEq(l.name, obs.layer));
                return {
                  pos: obs.pos,
                  extent: obs.extent,
                  heading: obs.heading,
                  ty: obs.ty,
                  visible: layer.visible,
                  idx,
                };
              }).filter((obs) => obs.visible),
            });
            window.ue.preview.printlog(payload);
          }}>push</button>
          <button onClick={() => {
            window.ue.preview.pull("");
          }}>pull</button>

          <button onClick={() => {
            this.setState({
              obstacle_specs: [],
              spawnareas: [],
              enemies_info: [],
              world: {
                width: 800, height: 800
              }
            })
          }}>reset</button>
          <input type="checkbox" checked={optShowIndex} onChange={() => this.setState({ optShowIndex: !optShowIndex })} />show index
          <input type="checkbox" checked={optEditmodePoint} onChange={() => this.setState({ optEditmodePoint: !optEditmodePoint })} />new editmode
          <input type="checkbox" checked={optShowGrid} onChange={() => this.setState({ optShowGrid: !optShowGrid })} />show grid
          <input type="checkbox" checked={optSnapping} onChange={() => this.setState({ optSnapping: !optSnapping })} />snap mode
          <button onClick={() => {
            makePreset();
          }}>run</button>
        </div>
        <canvas id="canvas" width={width} height={height}
          ref={this.canvasRef}
        ></canvas>

        <div className="box">
          <p>doors</p>
          {obstacle_specs.map((o, i) => {
            if (!this.layerState(o.layer).visible) {
              return null;
            }
            if (o.ty !== 'door') {
              return null;
            }
            return <div key={i}>
              #{i} <input type="text" style={{ width: 400 }} defaultValue={o.doorname} onChange={(e) => {
                let doorname = e.target.value;
                this.setState({
                  obstacle_specs: update(obstacle_specs, {
                    [i]: { doorname: { $set: doorname } },
                  }),
                });
              }} />
            </div>;
          })}
        </div>

        <DivCanvas {...this.state} layerName={'plan0'} routeLayerName={'route0'} />

        <div className="box">
          <p>metadata</p>
          width: <input type="number" value={width} onChange={(e) => this.setState({ world: { width: 0 | e.target.value, height } })} />
          height: <input type="number" value={height} onChange={(e) => this.setState({ world: { width, height: 0 | e.target.value } })} />
          <br />
          x: <input type="number" defaultValue={transposeX} onChange={(e) => this.setState({ transposeX: 0 | e.target.value })} />
          y: <input type="number" defaultValue={transposeY} onChange={(e) => this.setState({ transposeY: 0 | e.target.value })} />
          <button onClick={() => this.transposeAll(transposeX, transposeY)}>transpose</button>
        </div>

        <div className="box">
          <p>save & load</p>

          <p>데이터: 선택/복사해서 저장하세요. 에디터에 불러오려면 붙여넣기하세요.</p>
          <textarea ref={this.textRef} onChange={(e) => {
            const text = this.textRef.current.value;
            this.setState(this.deserialize(text));
          }}></textarea>
          {this.selectTrigger()}
          {this.selectRandomObs()}
          <p>config</p>
          <textarea id={'config'} ref={this.configRef} readOnly></textarea>
          <button onClick={() => {
            const copy = document.getElementById('config');
            copy.select();
            document.execCommand("copy");
          }}> 복사</button>
        </div>
      </div>
    );
  }
}

export class Editor extends React.Component {
  render() {
    const man = `마우스 왼쪽 클릭 드래그: 오브젝트 생성하기 / 옮기기
마우스 휠: 회전
마우스 오름쪽 클릭: 삭제하기, 이동하기 (moveTo 켜짐), 스폰 방향 삭제하기 (alt)
마우스 휠 클릭: 종류 바꾸기
alt-click: 크기 바꾸기
ctrl-click: 복제하기, 선택한 레이어로 복사하기 (moveTo 켜짐)
shift-click: 한 꼭짓점 잡고 높이 혹은 너비만 바꾸기

w: mark wip
spawnarea 1:effect_ty
          2:triggers
          3:team0 spawn`;
    return <>
      <pre>{man}</pre>
      <Canv {...this.props} />
    </>;
  }
}

export function SvgPolicyIcon(props) {
  const { svgKey, ty, sizeOverride } = props;
  const size = sizeOverride || 34;
  switch (svgKey) {
    case 'default':
      return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 34 34" fill="none">
        <path d="M31.9393 16.9999C31.9393 25.2507 25.2507 31.9393 16.9999 31.9393C8.74914 31.9393 2.06055 25.2507 2.06055 16.9999C2.06055 8.74914 8.74914 2.06055 16.9999 2.06055C25.2507 2.06055 31.9393 8.74914 31.9393 16.9999Z" fill="#E69800" />
        <path fillRule="evenodd" clipRule="evenodd" d="M17 3.09088C9.31819 3.09088 3.09088 9.31819 3.09088 17C3.09088 24.6817 9.31819 30.9091 17 30.9091C24.6817 30.9091 30.9091 24.6817 30.9091 17C30.9091 9.31819 24.6817 3.09088 17 3.09088ZM1.03027 17C1.03027 8.18015 8.18015 1.03027 17 1.03027C25.8198 1.03027 32.9697 8.18015 32.9697 17C32.9697 25.8198 25.8198 32.9697 17 32.9697C8.18015 32.9697 1.03027 25.8198 1.03027 17Z" fill="#FAA500" />
        <path d="M10.8183 11.6425C10.1355 11.6425 9.58192 12.196 9.58192 12.8788C9.58192 13.5617 10.1355 14.1152 10.8183 14.1152C11.5011 14.1152 12.0546 13.5617 12.0546 12.8788C12.0546 12.196 11.5011 11.6425 10.8183 11.6425Z" fill="#4C1E04" />
        <path fillRule="evenodd" clipRule="evenodd" d="M12.0546 7.0061H9.58192V8.73081C8.18606 9.14627 7.08572 10.2466 6.67026 11.6425L4.94556 11.6425V14.1152L6.67026 14.1152C7.08572 15.5111 8.18606 16.6114 9.58192 17.0268V18.7516H12.0546V17.0268C13.4505 16.6114 14.5508 15.5111 14.9663 14.1152H16.691V11.6425H14.9663C14.5508 10.2466 13.4505 9.14627 12.0546 8.73081V7.0061ZM8.44859 12.8788C8.44859 11.5701 9.50954 10.5091 10.8183 10.5091C12.127 10.5091 13.188 11.5701 13.188 12.8788C13.188 14.1876 12.127 15.2485 10.8183 15.2485C9.50954 15.2485 8.44859 14.1876 8.44859 12.8788Z" fill="#4C1E04" />
        <path d="M24.2122 13.7031C23.5294 13.7031 22.9759 14.2566 22.9759 14.9394C22.9759 15.6223 23.5294 16.1758 24.2122 16.1758C24.895 16.1758 25.4486 15.6223 25.4486 14.9394C25.4486 14.2566 24.895 13.7031 24.2122 13.7031Z" fill="#4C1E04" />
        <path fillRule="evenodd" clipRule="evenodd" d="M25.4486 9.06671H22.9759V10.7914C21.58 11.2069 20.4797 12.3072 20.0642 13.7031L18.3395 13.7031V16.1758H20.0642C20.4797 17.5717 21.58 18.672 22.9759 19.0875V20.8122H25.4486V19.0875C26.8444 18.672 27.9448 17.5717 28.3602 16.1758H30.085V13.7031H28.3602C27.9448 12.3072 26.8444 11.2069 25.4486 10.7914V9.06671ZM21.8425 14.9394C21.8425 13.6307 22.9035 12.5697 24.2122 12.5697C25.521 12.5697 26.5819 13.6307 26.5819 14.9394C26.5819 16.2482 25.521 17.3091 24.2122 17.3091C22.9035 17.3091 21.8425 16.2482 21.8425 14.9394Z" fill="#4C1E04" />
        <path d="M17.0001 22.9758C16.3173 22.9758 15.7637 23.5293 15.7637 24.2122C15.7637 24.895 16.3173 25.4485 17.0001 25.4485C17.6829 25.4485 18.2365 24.895 18.2365 24.2122C18.2365 23.5293 17.6829 22.9758 17.0001 22.9758Z" fill="#4C1E04" />
        <path fillRule="evenodd" clipRule="evenodd" d="M18.2365 18.3394H15.7637V20.0641C14.3679 20.4796 13.2675 21.5799 12.8521 22.9758H11.1274L11.1274 25.4485L12.8521 25.4485C13.2675 26.8444 14.3679 27.9447 15.7637 28.3602L15.7637 30.0849H18.2365L18.2365 28.3602C19.6323 27.9447 20.7327 26.8444 21.1481 25.4485H22.8728V22.9758H21.1481C20.7327 21.5799 19.6323 20.4796 18.2365 20.0641V18.3394ZM14.6304 24.2122C14.6304 22.9034 15.6914 21.8425 17.0001 21.8425C18.3088 21.8425 19.3698 22.9034 19.3698 24.2122C19.3698 25.5209 18.3088 26.5819 17.0001 26.5819C15.6914 26.5819 14.6304 25.5209 14.6304 24.2122Z" fill="#4C1E04" />
      </svg>;
      break;
    case 'focus':
      return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 34 34" fill="none">
        <path d="M31.9393 16.9999C31.9393 25.2507 25.2507 31.9393 16.9999 31.9393C8.74914 31.9393 2.06055 25.2507 2.06055 16.9999C2.06055 8.74914 8.74914 2.06055 16.9999 2.06055C25.2507 2.06055 31.9393 8.74914 31.9393 16.9999Z" fill="#E69800" />
        <path fillRule="evenodd" clipRule="evenodd" d="M17 3.09088C9.31819 3.09088 3.09088 9.31819 3.09088 17C3.09088 24.6817 9.31819 30.9091 17 30.9091C24.6817 30.9091 30.9091 24.6817 30.9091 17C30.9091 9.31819 24.6817 3.09088 17 3.09088ZM1.03027 17C1.03027 8.18015 8.18015 1.03027 17 1.03027C25.8198 1.03027 32.9697 8.18015 32.9697 17C32.9697 25.8198 25.8198 32.9697 17 32.9697C8.18015 32.9697 1.03027 25.8198 1.03027 17Z" fill="#FAA500" />
        <path d="M8.72451 6.18176L10.6354 10.6354L6.18176 8.72451L8.72451 6.18176Z" fill="#4C1E04" />
        <path fillRule="evenodd" clipRule="evenodd" d="M23.3645 10.6354C21.7357 9.0066 19.4854 7.99912 16.9999 7.99912C14.5144 7.99912 12.2642 9.00658 10.6354 10.6354C9.00657 12.2642 7.99912 14.5144 7.99912 16.9999C7.99912 20.3315 9.80918 23.2403 12.4995 24.7966C13.8234 25.5624 15.3605 26.0007 16.9999 26.0007C18.5534 26.0007 20.0149 25.6072 21.2902 24.9144C24.096 23.3902 26.0007 20.4175 26.0007 16.9999C26.0007 14.5144 24.9933 12.2643 23.3645 10.6354ZM12.9861 21.4059C12.7935 21.6001 12.6529 21.838 12.5737 22.0971C11.1497 20.8594 10.2493 19.0348 10.2493 16.9999C10.2493 13.2717 13.2717 10.2493 16.9999 10.2493C20.7282 10.2493 23.7505 13.2717 23.7505 16.9999C23.7505 19.0348 22.8502 20.8594 21.4261 22.0971C21.3469 21.838 21.2064 21.6001 21.0137 21.4059L18.5131 19.6845C19.792 19.0954 20.6821 17.7825 20.6821 16.2575C20.6821 14.182 19.0335 12.4995 16.9999 12.4995C14.9663 12.4995 13.3178 14.182 13.3178 16.2575C13.3178 17.7825 14.2078 19.0954 15.4867 19.6845L12.9861 21.4059Z" fill="#4C1E04" />
        <path d="M23.3645 10.6354L27.8181 8.72456L25.2754 6.18181L23.3645 10.6354Z" fill="#4C1E04" />
        <path d="M6.1818 25.2754L10.6354 23.3645L8.72455 27.8181L6.1818 25.2754Z" fill="#4C1E04" />
        <path d="M23.3645 23.3644L27.8181 25.2753L25.2753 27.8181L23.3645 23.3644Z" fill="#4C1E04" />
      </svg>;
      break;
    case 'riskdir':
      return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 34 34" fill="none">
        <path d="M31.4242 2.57581L2.57571 2.57581L2.57571 26.6162L17 32.4546L31.4242 26.6162L31.4242 2.57581Z" fill="#4FBC88" />
        <path fillRule="evenodd" clipRule="evenodd" d="M32.4546 1.54541L32.4546 27.3106L17 33.566L1.5455 27.3106L1.5455 1.54541L32.4546 1.54541ZM30.394 3.60602L3.60611 3.60602L3.60611 25.9216L17 31.343L30.394 25.9216L30.394 3.60602Z" fill="#63D69F" />
        <path fillRule="evenodd" clipRule="evenodd" d="M4.55945 8.19514L16.9385 21.804L20.6169 4.07702C19.4141 3.84162 18.1712 3.71826 16.8995 3.71826C12.2034 3.71826 7.9001 5.40046 4.55945 8.19514ZM13.9016 11.6296L14.4324 13.6108L15.3927 13.3534L14.8619 11.3723C15.6332 10.9371 16.0438 10.0211 15.804 9.12615C15.5197 8.0654 14.4294 7.4359 13.3687 7.72013C12.3079 8.00436 11.6784 9.09468 11.9626 10.1554C12.2024 11.0504 13.016 11.6384 13.9016 11.6296Z" fill="#007251" />
        <path d="M27.06 15.0774L29.4406 20.401L27.2544 20.1748C26.1046 24.0433 23.5982 26.229 21.3468 27.4617C21.4668 26.9564 21.4578 26.4134 21.2944 25.881C21.2684 25.7964 21.2391 25.7138 21.2065 25.6332C22.915 24.5533 24.6601 22.8227 25.5808 20.0017L23.6399 19.8009L27.06 15.0774Z" fill="#007251" />
        <path fillRule="evenodd" clipRule="evenodd" d="M18.3877 3.77496L16.925 21.742L6.3938 6.83631C9.41436 4.86441 13.023 3.71826 16.8994 3.71826C17.4002 3.71826 17.8965 3.73739 18.3877 3.77496ZM13.9015 11.6296L14.4324 13.6108L15.3927 13.3534L14.8618 11.3723C15.6331 10.9371 16.0437 10.0211 15.8039 9.12614C15.5197 8.06539 14.4294 7.43589 13.3686 7.72012C12.3079 8.00435 11.6784 9.09467 11.9626 10.1554C12.2024 11.0504 13.016 11.6383 13.9015 11.6296Z" fill="#033728" />
        <path d="M21.6862 16.212L28.7152 9.18304L28.7048 5.67843L19.9287 14.4545L21.6862 16.212Z" fill="#033728" />
        <path d="M10.0801 27.8181L15.3927 22.6359L13.6352 20.8784L7.58358 26.7996L10.0801 27.8181Z" fill="#033728" />
        <path d="M17.7167 22.8193L18.3187 24.7801C19.2033 24.7394 20.0376 25.2977 20.3095 26.1834C20.6318 27.2332 20.042 28.3455 18.9922 28.6678C17.9424 28.9901 16.8301 28.4003 16.5078 27.3505C16.2359 26.4648 16.6132 25.5346 17.3682 25.0719L16.7663 23.1111L17.7167 22.8193Z" fill="#033728" />
      </svg>;
      break;
    case 'door_entry':
      return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 34 34" fill="none">
        <path d="M31.4242 2.57581L2.57571 2.57581L2.57571 26.6162L17 32.4546L31.4242 26.6162L31.4242 2.57581Z" fill="#4FBC88" />
        <path fillRule="evenodd" clipRule="evenodd" d="M32.4546 1.54541L32.4546 27.3106L17 33.566L1.5455 27.3106L1.5455 1.54541L32.4546 1.54541ZM30.394 3.60602L3.60611 3.60602L3.60611 25.9216L17 31.343L30.394 25.9216L30.394 3.60602Z" fill="#63D69F" />
        <path d="M17.6094 19.8617V21.9128C18.467 22.1335 19.1007 22.912 19.1007 23.8385C19.1007 24.9367 18.2105 25.8269 17.1123 25.8269C16.0141 25.8269 15.1239 24.9367 15.1239 23.8385C15.1239 22.912 15.7576 22.1335 16.6152 21.9128V19.8617H17.6094Z" fill="#033728" />
        <path d="M9.4342 24.1322H3.724V21.5564H9.4342V24.1322Z" fill="#033728" />
        <path d="M30.5119 24.1322L24.5761 24.1322L24.5761 21.5564L30.5119 21.5564L30.5119 24.1322Z" fill="#033728" />
        <path d="M12.6964 23.9439L12.8449 21.8982L11.8533 21.8262L11.7048 23.8719C10.8335 24.03 10.1451 24.7606 10.0781 25.6847C9.99857 26.78 10.822 27.7323 11.9173 27.8118C13.0126 27.8913 13.965 27.0678 14.0445 25.9725C14.1115 25.0484 13.5358 24.2261 12.6964 23.9439Z" fill="#033728" />
        <path d="M21.8272 23.3632L21.9908 25.4077C22.8633 25.5593 23.5571 26.2848 23.631 27.2084C23.7186 28.303 22.9022 29.2615 21.8075 29.3491C20.7129 29.4367 19.7544 28.6203 19.6668 27.5256C19.5929 26.602 20.1625 25.7754 20.9998 25.487L20.8362 23.4425L21.8272 23.3632Z" fill="#033728" />
        <path d="M19.9684 9.64085L17.0526 4.59045L14.1367 9.64085H16.2731L16.2731 18.3111H17.9215L17.9215 9.64085H19.9684Z" fill="#007251" />
        <path d="M21.8773 21.6977C21.8016 20.61 21.7426 19.678 21.7502 18.8736C21.7579 18.0658 21.8333 17.4475 21.9945 16.9747C22.1484 16.5233 22.3767 16.2132 22.7133 15.9877C22.9807 15.8086 23.354 15.6578 23.8852 15.5629L24.2478 17.739L28.7503 14.0328L23.2894 11.9866L23.6143 13.9367C22.9087 14.0608 22.3012 14.2795 21.7958 14.6182C21.1139 15.075 20.686 15.7043 20.4342 16.4427C20.1897 17.1597 20.1101 17.9817 20.1018 18.858C20.0935 19.738 20.1578 20.7342 20.2328 21.8122L21.8773 21.6977Z" fill="#007251" />
        <path d="M11.8434 18.6605C11.8003 19.1421 11.7543 19.6611 11.716 20.2116L13.3605 20.3261C13.3975 19.7937 13.4422 19.2893 13.4853 18.8076L13.4932 18.7203C13.5333 18.273 13.572 17.8419 13.6 17.437C13.6589 16.5875 13.6781 15.7755 13.5419 15.0678C13.4006 14.3333 13.0818 13.6609 12.4383 13.1654C11.9761 12.8096 11.3992 12.5865 10.7188 12.4598L10.8787 10.335L5.62378 12.8638L10.4412 16.1503L10.5942 14.1172C10.995 14.2095 11.2572 14.3365 11.4326 14.4716C11.6749 14.6581 11.8356 14.9245 11.9231 15.3793C12.0158 15.8609 12.0133 16.4887 11.9555 17.323C11.9286 17.7105 11.8914 18.1258 11.8508 18.5785L11.8434 18.6605Z" fill="#007251" />
      </svg>;
      break;
    case 'throwable_cooking':
      return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 34 34" fill="none">
        <path d="M31.4242 2.57581L2.57571 2.57581L2.57571 26.6162L17 32.4546L31.4242 26.6162L31.4242 2.57581Z" fill="#4FBC88" />
        <path fillRule="evenodd" clipRule="evenodd" d="M32.4546 1.54541L32.4546 27.3106L17 33.566L1.5455 27.3106L1.5455 1.54541L32.4546 1.54541ZM30.394 3.60602L3.60611 3.60602L3.60611 25.9216L17 31.343L30.394 25.9216L30.394 3.60602Z" fill="#63D69F" />
        <path fillRule="evenodd" clipRule="evenodd" d="M29.5187 18.174L27.4721 17.9342C27.5198 17.5268 27.5444 17.1115 27.5444 16.6896C27.5444 16.2677 27.5198 15.8524 27.4721 15.445L29.5187 15.2052C29.5757 15.6921 29.605 16.1874 29.605 16.6896C29.605 17.1918 29.5757 17.6871 29.5187 18.174ZM29.3648 14.2264L27.3434 14.6263C27.1802 13.8016 26.921 13.0116 26.5776 12.2681L28.4484 11.4042C28.8592 12.2938 29.1696 13.2394 29.3648 14.2264ZM27.9978 10.5215L26.2008 11.53C25.7931 10.8035 25.3016 10.1295 24.7391 9.52131L26.2519 8.12221C26.9233 8.84812 27.5104 9.65306 27.9978 10.5215ZM25.5511 7.42137L24.152 8.93419C23.5438 8.37171 22.8698 7.88018 22.1433 7.47246L23.1518 5.67551C24.0202 6.1629 24.8252 6.75003 25.5511 7.42137ZM22.2691 5.22487L21.4052 7.09564C20.6617 6.7523 19.8717 6.49306 19.047 6.3299L19.4469 4.30847C20.4339 4.50372 21.3794 4.81404 22.2691 5.22487ZM18.4681 4.15461L18.2283 6.20121C17.8208 6.15348 17.4056 6.12884 16.9837 6.12884C16.5618 6.12884 16.1465 6.15348 15.739 6.20121L15.4993 4.15461C15.9861 4.09757 16.4815 4.06824 16.9837 4.06824C17.4859 4.06824 17.9812 4.09757 18.4681 4.15461ZM4.44867 15.2052C4.39163 15.6921 4.3623 16.1874 4.3623 16.6896C4.3623 17.1918 4.39163 17.6871 4.44867 18.174L6.49528 17.9342C6.44755 17.5268 6.42291 17.1115 6.42291 16.6896C6.42291 16.2677 6.44755 15.8524 6.49528 15.445L4.44867 15.2052ZM4.60254 19.1528L6.62397 18.7529C6.78713 19.5776 7.04637 20.3676 7.38971 21.1111L5.51894 21.975C5.10811 21.0854 4.79779 20.1398 4.60254 19.1528ZM5.96958 22.8577L7.76653 21.8492C8.17425 22.5757 8.66577 23.2497 9.22825 23.8579L7.71544 25.257C7.04409 24.5311 6.45696 23.7262 5.96958 22.8577ZM8.41628 25.9578L9.81538 24.445C10.4236 25.0075 11.0976 25.499 11.8241 25.9067L10.8156 27.7037C9.94712 27.2163 9.14219 26.6292 8.41628 25.9578ZM11.6983 28.1543L12.5621 26.2836C13.3057 26.6269 14.0956 26.8862 14.9204 27.0493L14.5205 29.0707C13.5335 28.8755 12.5879 28.5652 11.6983 28.1543ZM15.4993 29.2246L15.739 27.178C16.1465 27.2257 16.5618 27.2504 16.9837 27.2504C17.4056 27.2504 17.8208 27.2257 18.2283 27.178L18.4681 29.2246C17.9812 29.2816 17.4859 29.311 16.9837 29.311C16.4815 29.311 15.9861 29.2816 15.4993 29.2246ZM19.4469 29.0707L19.047 27.0493C19.8717 26.8862 20.6617 26.6269 21.4052 26.2836L22.2691 28.1543C21.3794 28.5652 20.4338 28.8755 19.4469 29.0707ZM23.1518 27.7037L22.1433 25.9068C22.8698 25.499 23.5438 25.0075 24.152 24.445L25.5511 25.9578C24.8252 26.6292 24.0202 27.2163 23.1518 27.7037ZM26.2519 25.257L24.7391 23.8579C25.3016 23.2497 25.7931 22.5757 26.2008 21.8492L27.9978 22.8577C27.5104 23.7262 26.9233 24.5311 26.2519 25.257ZM28.4484 21.975L26.5776 21.1111C26.921 20.3676 27.1802 19.5776 27.3434 18.7529L29.3648 19.1528C29.1696 20.1398 28.8592 21.0854 28.4484 21.975Z" fill="#007251" />
        <path d="M17.1496 9.97891L15.2521 11.0744L15.8726 12.1492C18.2308 11.9108 20.6185 12.9631 22.0033 15.0294L10.6403 21.6231C9.47091 19.2694 9.84019 16.5083 11.4227 14.5545L10.3583 11.5031L10.6081 11.3589L10.6069 11.3567L16.8312 7.76306L17.2307 7.85582C19.0999 8.28989 21.4495 9.31431 23.7761 11.6884L22.2918 13.143C20.4347 11.248 18.6103 10.3817 17.1496 9.97891Z" fill="#033728" />
        <path d="M11.391 22.8042C13.3863 25.313 16.97 26.0516 19.8298 24.4005C22.6982 22.7445 23.8487 19.2557 22.6562 16.2673L11.391 22.8042Z" fill="#033728" />
      </svg>;
      break;
    case 'forbid':
      if (ty === 'throwopts') {
        return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 34 34" fill="none">
          <path d="M12.3636 2.06055H21.6364C24.5387 2.06055 27.8182 3.606 29.8788 5.66661L28.8485 8.24236C26.6693 6.44388 25.1804 5.68392 21.6364 5.15146V7.04153C26.4582 8.90282 29.8788 13.5822 29.8788 19.0605C29.8788 26.1733 24.1128 31.9393 17 31.9393C9.88725 31.9393 4.12122 26.1733 4.12122 19.0605C4.12122 13.5822 7.54176 8.90282 12.3636 7.04153V2.06055Z" fill="#78644B" />
          <path fillRule="evenodd" clipRule="evenodd" d="M11.3334 1.03027H21.6364C24.8519 1.03027 28.3837 2.71442 30.6074 4.9381L31.0878 5.4185L29.2811 9.93523L28.1927 9.03703C27.1372 8.16588 26.3029 7.5917 25.3373 7.16434C24.6229 6.84818 23.8069 6.59935 22.7378 6.38664C27.5556 8.57116 30.9091 13.4231 30.9091 19.0606C30.9091 26.7424 24.6818 32.9697 17 32.9697C9.31826 32.9697 3.09094 26.7424 3.09094 19.0606C3.09094 13.3954 6.47747 8.52352 11.3334 6.35465V1.03027ZM13.394 3.09088V7.74826L12.7347 8.00274C8.29684 9.71579 5.15155 14.0221 5.15155 19.0606C5.15155 25.6043 10.4563 30.9091 17 30.9091C23.5438 30.9091 28.8485 25.6043 28.8485 19.0606C28.8485 14.0221 25.7032 9.71579 21.2654 8.00274L20.6061 7.74826V3.95482L21.7895 4.13262C23.6081 4.40585 24.9664 4.74682 26.1712 5.28003C26.9745 5.63555 27.6853 6.06589 28.4012 6.58647L28.6606 5.93796C26.7583 4.276 24.0129 3.09088 21.6364 3.09088H13.394Z" fill="#867045" />
          <path fillRule="evenodd" clipRule="evenodd" d="M12.7905 11.9368L24.447 23.5934L21.5329 26.5075L9.87634 14.851L12.7905 11.9368Z" fill="#C9AD83" />
          <path fillRule="evenodd" clipRule="evenodd" d="M24.447 14.851L12.7904 26.5076L9.87627 23.5934L21.5328 11.9369L24.447 14.851Z" fill="#C9AD83" />
        </svg>;
      } else if (ty === 'healopts') {
        return <svg viewBox="0 0 34 34" width={size} height={size} fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9.77314 3.09094C13.0849 3.09094 15.9089 5.17897 16.9999 8.11014C18.091 5.17897 20.915 3.09094 24.2267 3.09094C28.4109 3.09094 31.8165 6.42404 31.9323 10.5795L31.9331 10.6132C31.9346 10.674 31.9353 10.7351 31.9353 10.7963L31.9354 10.8001C31.961 12.7575 32.025 17.6498 26.3031 23.2834C25.6538 24.0053 24.9072 24.6574 24.199 25.276C23.7508 25.6675 23.3181 26.0455 22.9351 26.4192C19.8856 28.9532 17.99 30.9091 17 30.9091C16.0099 30.9091 14.1143 28.9532 11.0648 26.4192C10.6818 26.0455 10.249 25.6675 9.80091 25.276C9.09273 24.6574 8.34613 24.0053 7.6968 23.2834C1.97489 17.6498 2.03889 12.7575 2.0645 10.8001L2.06455 10.7963C2.06457 10.7351 2.0653 10.674 2.06673 10.6132L2.0676 10.5795C2.18338 6.42404 5.58895 3.09094 9.77314 3.09094Z" fill="#78644B" transform="matrix(0.9999999999999999, 0, 0, 0.9999999999999999, -4.440892098500626e-16, 0)" />
          <path fillRule="evenodd" clipRule="evenodd" d="M1.03778 10.5507C1.16906 5.83902 5.02979 2.06055 9.77323 2.06055C12.7783 2.06055 15.4277 3.57686 17 5.88434C18.5724 3.57686 21.2217 2.06055 24.2268 2.06055C28.9703 2.06055 32.831 5.83902 32.9623 10.5507L32.9623 10.553L32.9633 10.5888C32.9648 10.6554 32.9657 10.7222 32.9657 10.7891L32.9657 10.7912C32.9789 11.7988 33.0031 13.6456 32.2558 15.9775C31.5084 18.3097 30.0076 21.0755 27.048 23.9958C26.3622 24.7545 25.5854 25.433 24.8974 26.034L24.8769 26.0519C24.4238 26.4476 24.0136 26.8064 23.6548 27.1565L23.6253 27.1853L23.5937 27.2116C22.8405 27.8374 22.1575 28.4285 21.5358 28.9679C21.4955 29.0029 21.4554 29.0377 21.4155 29.0723C20.8446 29.5678 20.3196 30.0234 19.8501 30.4098C19.3519 30.8199 18.8818 31.181 18.4538 31.4428C18.0565 31.6857 17.5463 31.9393 17 31.9393C16.4538 31.9393 15.9436 31.6857 15.5463 31.4428C15.1183 31.181 14.6482 30.8199 14.15 30.4098C13.6804 30.0233 13.1553 29.5676 12.5842 29.072L12.4643 28.9679C11.8426 28.4285 11.1596 27.8374 10.4064 27.2116L10.3747 27.1853L10.3453 27.1565C9.98645 26.8064 9.57623 26.4476 9.1232 26.0519L9.10267 26.034C8.4146 25.433 7.63785 24.7545 6.95204 23.9958C3.99241 21.0755 2.49166 18.3097 1.74427 15.9775C0.996966 13.6456 1.02113 11.7988 1.03432 10.7912L1.03434 10.7891C1.03441 10.7222 1.03523 10.6554 1.0368 10.5888L1.03686 10.5866L1.03772 10.5529L1.03778 10.5507ZM3.09762 10.607C3.09761 10.6074 3.0976 10.6078 3.09759 10.6081L3.09684 10.6373C3.09559 10.6902 3.09496 10.7432 3.09495 10.7964L3.09494 10.8096L3.09481 10.8134C3.08237 11.7643 3.06296 13.3403 3.70657 15.3486C4.34941 17.3546 5.67202 19.8438 8.41974 22.5491L8.44202 22.571L8.46293 22.5943C9.0638 23.2623 9.76263 23.8744 10.4788 24.5L10.4909 24.5106C10.9195 24.8849 11.3588 25.2687 11.7545 25.6526C12.5137 26.2841 13.2008 26.8788 13.8148 27.4115C13.8525 27.4442 13.8898 27.4767 13.9269 27.5089C14.5061 28.0115 15.0116 28.4502 15.4595 28.8188C15.9402 29.2145 16.3202 29.5007 16.6214 29.6849C16.8459 29.8221 16.9634 29.8624 17 29.8741C17.0367 29.8624 17.1542 29.8221 17.3787 29.6849C17.6798 29.5007 18.0599 29.2145 18.5405 28.8189C18.9884 28.4502 19.4937 28.0117 20.0728 27.5092L20.1853 27.4115C20.7992 26.8788 21.4863 26.2841 22.2456 25.6526C22.6412 25.2687 23.0805 24.885 23.509 24.5107L23.5213 24.5C24.2374 23.8744 24.9363 23.2623 25.5371 22.5943L25.558 22.571L25.5803 22.5491C28.328 19.8438 29.6507 17.3546 30.2935 15.3486C30.9371 13.3403 30.9177 11.7643 30.9053 10.8134L30.905 10.7964C30.905 10.7437 30.9045 10.6912 30.9033 10.6388C30.9032 10.6383 30.9032 10.6378 30.9032 10.6374L30.9025 10.6081C30.8022 7.00887 27.8518 4.12115 24.2268 4.12115C21.3592 4.12115 18.9114 5.92861 17.9656 8.46947L17 11.0635L16.0345 8.46947C15.0887 5.92861 12.6408 4.12115 9.77323 4.12115C6.14864 4.12115 3.19846 7.0083 3.09762 10.607Z" fill="#867045" transform="matrix(0.9999999999999999, 0, 0, 0.9999999999999999, -4.440892098500626e-16, 0)" />
          <path fillRule="evenodd" clipRule="evenodd" d="M12.7905 9.9368L24.447 21.5934L21.5329 24.5075L9.87634 12.851L12.7905 9.9368Z" fill="#C9AD83" />
          <path fillRule="evenodd" clipRule="evenodd" d="M24.447 12.851L12.7904 24.5076L9.87627 21.5934L21.5328 9.9369L24.447 12.851Z" fill="#C9AD83" />
        </svg>
      }
      break;
    case 'allow':
      return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 34 34" fill="none">
        <path d="M12.3636 2.06055H21.6364C24.5387 2.06055 27.8182 3.606 29.8788 5.66661L28.8485 8.24236C26.6693 6.44388 25.1804 5.68392 21.6364 5.15146V7.04153C26.4582 8.90282 29.8788 13.5822 29.8788 19.0605C29.8788 26.1733 24.1128 31.9393 17 31.9393C9.88725 31.9393 4.12122 26.1733 4.12122 19.0605C4.12122 13.5822 7.54176 8.90282 12.3636 7.04153V2.06055Z" fill="#E69800" />
        <path fillRule="evenodd" clipRule="evenodd" d="M11.3334 1.03027H21.6364C24.8519 1.03027 28.3837 2.71442 30.6074 4.9381L31.0878 5.4185L29.2811 9.93523L28.1927 9.03703C27.1372 8.16588 26.3029 7.5917 25.3373 7.16434C24.6229 6.84818 23.8069 6.59935 22.7378 6.38664C27.5556 8.57116 30.9091 13.4231 30.9091 19.0606C30.9091 26.7424 24.6818 32.9697 17 32.9697C9.31826 32.9697 3.09094 26.7424 3.09094 19.0606C3.09094 13.3954 6.47747 8.52352 11.3334 6.35465V1.03027ZM13.394 3.09088V7.74826L12.7347 8.00274C8.29684 9.71579 5.15155 14.0221 5.15155 19.0606C5.15155 25.6043 10.4563 30.9091 17 30.9091C23.5438 30.9091 28.8485 25.6043 28.8485 19.0606C28.8485 14.0221 25.7032 9.71579 21.2654 8.00274L20.6061 7.74826V3.95482L21.7895 4.13262C23.6081 4.40585 24.9664 4.74682 26.1712 5.28003C26.9745 5.63555 27.6853 6.06589 28.4012 6.58647L28.6606 5.93796C26.7583 4.276 24.0129 3.09088 21.6364 3.09088H13.394Z" fill="#FAA500" />
        <path fillRule="evenodd" clipRule="evenodd" d="M25.6737 15.8768L15.0101 26.6066L8.85498 20.5266L11.7512 17.5946L14.9833 20.7873L22.7506 12.9717L25.6737 15.8768Z" fill="#4C1E04" />
      </svg>;
      break;
    case 'conservative':
      return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 34 34" fill="none">
        <path d="M9.77314 3.09094C13.0849 3.09094 15.9089 5.17897 16.9999 8.11014C18.091 5.17897 20.915 3.09094 24.2267 3.09094C28.4109 3.09094 31.8165 6.42404 31.9323 10.5795L31.9331 10.6132C31.9346 10.674 31.9353 10.7351 31.9353 10.7963L31.9354 10.8001C31.961 12.7575 32.025 17.6498 26.3031 23.2834C25.6538 24.0053 24.9072 24.6574 24.199 25.276C23.7508 25.6675 23.3181 26.0455 22.9351 26.4192C19.8856 28.9532 17.99 30.9091 17 30.9091C16.0099 30.9091 14.1143 28.9532 11.0648 26.4192C10.6818 26.0455 10.249 25.6675 9.80091 25.276C9.09273 24.6574 8.34613 24.0053 7.6968 23.2834C1.97489 17.6498 2.03889 12.7575 2.0645 10.8001L2.06455 10.7963C2.06457 10.7351 2.0653 10.674 2.06673 10.6132L2.0676 10.5795C2.18338 6.42404 5.58895 3.09094 9.77314 3.09094Z" fill="#E69800" />
        <path fillRule="evenodd" clipRule="evenodd" d="M1.03778 10.5507C1.16906 5.83902 5.02979 2.06055 9.77323 2.06055C12.7783 2.06055 15.4277 3.57686 17 5.88434C18.5724 3.57686 21.2217 2.06055 24.2268 2.06055C28.9703 2.06055 32.831 5.83902 32.9623 10.5507L32.9623 10.553L32.9633 10.5888C32.9648 10.6554 32.9657 10.7222 32.9657 10.7891L32.9657 10.7912C32.9789 11.7988 33.0031 13.6456 32.2558 15.9775C31.5084 18.3097 30.0076 21.0755 27.048 23.9958C26.3622 24.7545 25.5854 25.433 24.8974 26.034L24.8769 26.0519C24.4238 26.4476 24.0136 26.8064 23.6548 27.1565L23.6253 27.1853L23.5937 27.2116C22.8405 27.8374 22.1575 28.4285 21.5358 28.9679C21.4955 29.0029 21.4554 29.0377 21.4155 29.0723C20.8446 29.5678 20.3196 30.0234 19.8501 30.4098C19.3519 30.8199 18.8818 31.181 18.4538 31.4428C18.0565 31.6857 17.5463 31.9393 17 31.9393C16.4538 31.9393 15.9436 31.6857 15.5463 31.4428C15.1183 31.181 14.6482 30.8199 14.15 30.4098C13.6804 30.0233 13.1553 29.5676 12.5842 29.072L12.4643 28.9679C11.8426 28.4285 11.1596 27.8374 10.4064 27.2116L10.3747 27.1853L10.3453 27.1565C9.98645 26.8064 9.57623 26.4476 9.1232 26.0519L9.10267 26.034C8.4146 25.433 7.63785 24.7545 6.95204 23.9958C3.99241 21.0755 2.49166 18.3097 1.74427 15.9775C0.996966 13.6456 1.02113 11.7988 1.03432 10.7912L1.03434 10.7891C1.03441 10.7222 1.03523 10.6554 1.0368 10.5888L1.03686 10.5866L1.03772 10.5529L1.03778 10.5507ZM3.09762 10.607C3.09761 10.6074 3.0976 10.6078 3.09759 10.6081L3.09684 10.6373C3.09559 10.6902 3.09496 10.7432 3.09495 10.7964L3.09494 10.8096L3.09481 10.8134C3.08237 11.7643 3.06296 13.3403 3.70657 15.3486C4.34941 17.3546 5.67202 19.8438 8.41974 22.5491L8.44202 22.571L8.46293 22.5943C9.0638 23.2623 9.76263 23.8744 10.4788 24.5L10.4909 24.5106C10.9195 24.8849 11.3588 25.2687 11.7545 25.6526C12.5137 26.2841 13.2008 26.8788 13.8148 27.4115C13.8525 27.4442 13.8898 27.4767 13.9269 27.5089C14.5061 28.0115 15.0116 28.4502 15.4595 28.8188C15.9402 29.2145 16.3202 29.5007 16.6214 29.6849C16.8459 29.8221 16.9634 29.8624 17 29.8741C17.0367 29.8624 17.1542 29.8221 17.3787 29.6849C17.6798 29.5007 18.0599 29.2145 18.5405 28.8189C18.9884 28.4502 19.4937 28.0117 20.0728 27.5092L20.1853 27.4115C20.7992 26.8788 21.4863 26.2841 22.2456 25.6526C22.6412 25.2687 23.0805 24.885 23.509 24.5107L23.5213 24.5C24.2374 23.8744 24.9363 23.2623 25.5371 22.5943L25.558 22.571L25.5803 22.5491C28.328 19.8438 29.6507 17.3546 30.2935 15.3486C30.9371 13.3403 30.9177 11.7643 30.9053 10.8134L30.905 10.7964C30.905 10.7437 30.9045 10.6912 30.9033 10.6388C30.9032 10.6383 30.9032 10.6378 30.9032 10.6374L30.9025 10.6081C30.8022 7.00887 27.8518 4.12115 24.2268 4.12115C21.3592 4.12115 18.9114 5.92861 17.9656 8.46947L17 11.0635L16.0345 8.46947C15.0887 5.92861 12.6408 4.12115 9.77323 4.12115C6.14864 4.12115 3.19846 7.0083 3.09762 10.607Z" fill="#FAA500" />
        <path d="M3.09756 10.7584L3.09753 10.7594L3.09678 10.7893C3.09552 10.8434 3.09489 10.8976 3.09487 10.9521L3.09487 10.9646L3.09474 10.9684C3.08234 11.9336 3.06283 13.5393 3.70677 15.5849C4.33113 17.5683 5.59531 20.0115 8.17674 22.6667H25.8233C28.4047 20.0115 29.6689 17.5683 30.2933 15.5849C30.9371 13.5397 30.9177 11.9342 30.9053 10.9689L30.9051 10.9521C30.9051 10.8979 30.9045 10.844 30.9033 10.7902L30.9025 10.7594C30.8017 7.07606 27.8359 4.12122 24.1924 4.12122C21.3099 4.12122 18.8495 5.9708 17.8988 8.57082L17 11.0288L16.1013 8.57082C15.1506 5.9708 12.6902 4.12122 9.80771 4.12122C6.1645 4.12122 3.19889 7.07551 3.09756 10.7584Z" fill="#4C1E04" />
      </svg>;
      break;
    case 'aggressive':
      if (ty === 'healopts') {
        return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 34 34" fill="none">
          <path d="M9.77314 3.09094C13.0849 3.09094 15.9089 5.17897 16.9999 8.11014C18.091 5.17897 20.915 3.09094 24.2267 3.09094C28.4109 3.09094 31.8165 6.42404 31.9323 10.5795L31.9331 10.6132C31.9346 10.674 31.9353 10.7351 31.9353 10.7963L31.9354 10.8001C31.961 12.7575 32.025 17.6498 26.3031 23.2834C25.6538 24.0053 24.9072 24.6574 24.199 25.276C23.7508 25.6675 23.3181 26.0455 22.9351 26.4192C19.8856 28.9532 17.99 30.9091 17 30.9091C16.0099 30.9091 14.1143 28.9532 11.0648 26.4192C10.6818 26.0455 10.249 25.6675 9.80091 25.276C9.09273 24.6574 8.34613 24.0053 7.6968 23.2834C1.97489 17.6498 2.03889 12.7575 2.0645 10.8001L2.06455 10.7963C2.06457 10.7351 2.0653 10.674 2.06673 10.6132L2.0676 10.5795C2.18338 6.42404 5.58895 3.09094 9.77314 3.09094Z" fill="#E69800" />
          <path fillRule="evenodd" clipRule="evenodd" d="M1.03778 10.5507C1.16906 5.83902 5.02979 2.06055 9.77323 2.06055C12.7783 2.06055 15.4277 3.57686 17 5.88434C18.5724 3.57686 21.2217 2.06055 24.2268 2.06055C28.9703 2.06055 32.831 5.83902 32.9623 10.5507L32.9623 10.553L32.9633 10.5888C32.9648 10.6554 32.9656 10.7222 32.9657 10.7891L32.9657 10.7912C32.9789 11.7988 33.0031 13.6456 32.2558 15.9775C31.5084 18.3097 30.0076 21.0755 27.048 23.9958C26.3622 24.7545 25.5855 25.433 24.8974 26.034L24.8769 26.0519C24.4238 26.4476 24.0136 26.8064 23.6548 27.1565L23.6253 27.1853L23.5937 27.2116C22.8405 27.8374 22.1575 28.4285 21.5358 28.9679C21.4955 29.0028 21.4555 29.0375 21.4157 29.0721C20.8447 29.5676 20.3196 30.0233 19.8501 30.4098C19.3519 30.8199 18.8818 31.181 18.4538 31.4428C18.0565 31.6857 17.5463 31.9393 17 31.9393C16.4538 31.9393 15.9436 31.6857 15.5463 31.4428C15.1183 31.181 14.6482 30.8199 14.15 30.4098C13.6804 30.0233 13.1553 29.5675 12.5842 29.0719L12.4643 28.9679C11.8426 28.4285 11.1596 27.8374 10.4064 27.2116L10.3747 27.1853L10.3453 27.1565C9.98645 26.8064 9.57623 26.4476 9.1232 26.0519L9.10266 26.034C8.41459 25.433 7.63784 24.7545 6.95204 23.9958C3.99241 21.0755 2.49166 18.3097 1.74427 15.9775C0.996966 13.6456 1.02113 11.7988 1.03432 10.7912L1.03434 10.7891C1.03441 10.7222 1.03523 10.6554 1.0368 10.5888L1.03686 10.5866L1.03772 10.5529L1.03778 10.5507ZM3.09762 10.607C3.09761 10.6074 3.0976 10.6078 3.09759 10.6081L3.09684 10.6373C3.09559 10.6902 3.09496 10.7432 3.09495 10.7964L3.09494 10.8096L3.09481 10.8134C3.08237 11.7643 3.06296 13.3403 3.70657 15.3486C4.34941 17.3546 5.67202 19.8438 8.41974 22.5491L8.44202 22.571L8.46293 22.5943C9.0638 23.2623 9.76263 23.8744 10.4788 24.5L10.4909 24.5106C10.9195 24.8849 11.3588 25.2687 11.7545 25.6526C12.5137 26.2841 13.2008 26.8788 13.8148 27.4115C13.8525 27.4442 13.8898 27.4767 13.9269 27.5089C14.5061 28.0115 15.0116 28.4502 15.4595 28.8188C15.9402 29.2145 16.3202 29.5007 16.6214 29.6849C16.8459 29.8221 16.9634 29.8624 17 29.8741C17.0367 29.8624 17.1542 29.8221 17.3787 29.6849C17.6798 29.5007 18.0599 29.2145 18.5405 28.8189C18.9883 28.4503 19.4936 28.0118 20.0726 27.5093C20.1098 27.477 20.1474 27.4444 20.1853 27.4115C20.7992 26.8788 21.4863 26.2841 22.2456 25.6526C22.6412 25.2687 23.0805 24.885 23.509 24.5107L23.5213 24.5C24.2374 23.8744 24.9363 23.2623 25.5371 22.5943L25.558 22.571L25.5803 22.5491C28.328 19.8438 29.6507 17.3546 30.2935 15.3486C30.937 13.3406 30.9177 11.7649 30.9053 10.8139C30.9053 10.8138 30.9053 10.8141 30.9053 10.8139L30.905 10.7964C30.905 10.7435 30.9045 10.6908 30.9032 10.6383C30.9032 10.638 30.9033 10.6386 30.9032 10.6383L30.9025 10.6081C30.8022 7.00887 27.8518 4.12115 24.2268 4.12115C21.3592 4.12115 18.9114 5.92861 17.9656 8.46947L17 11.0635L16.0345 8.46947C15.0887 5.92861 12.6408 4.12115 9.77323 4.12115C6.14864 4.12115 3.19846 7.0083 3.09762 10.607Z" fill="#FAA500" />
          <path d="M4.12122 8.24243H15.9071C14.8601 5.80459 12.6138 4.12122 10.0141 4.12122C7.41308 4.12122 5.16767 5.80592 4.12122 8.24243Z" fill="#4C1E04" />
          <path d="M18.0929 8.24243H29.8788C28.8323 5.80592 26.5869 4.12122 23.986 4.12122C21.3862 4.12122 19.1399 5.80459 18.0929 8.24243Z" fill="#4C1E04" />
          <path d="M19.0607 12.2947H14.9394V16.4159H10.8182V20.5371H14.9394V24.6583H19.0607V20.5371H23.1819V16.4159H19.0607V12.2947Z" fill="#4C1E04" />
        </svg>;
      } else if (ty === 'firepolicy') {
        return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 33 33" fill="none">
          <path d="M31 16.5C31 24.5081 24.5081 31 16.5 31C8.49187 31 2 24.5081 2 16.5C2 8.49187 8.49187 2 16.5 2C24.5081 2 31 8.49187 31 16.5Z" fill="#E69800" />
          <path fillRule="evenodd" clipRule="evenodd" d="M16.5 3C9.04416 3 3 9.04416 3 16.5C3 23.9558 9.04416 30 16.5 30C23.9558 30 30 23.9558 30 16.5C30 9.04416 23.9558 3 16.5 3ZM1 16.5C1 7.93959 7.93959 1 16.5 1C25.0604 1 32 7.93959 32 16.5C32 25.0604 25.0604 32 16.5 32C7.93959 32 1 25.0604 1 16.5Z" fill="#FAA500" />
          <path d="M8.4332 7.32812C8.20702 7.32812 8.02562 7.51433 8.03488 7.74032C8.29422 14.074 9.7338 22.1418 17.4157 28.8046C17.4201 28.8084 17.4268 28.8086 17.4312 28.8048L17.4366 28.8003C17.4387 28.7986 17.4419 28.7989 17.4436 28.801C17.4454 28.8031 17.4485 28.8034 17.4506 28.8016L25.6627 22.0073L25.7148 21.9601C25.8847 21.8063 25.8908 21.5415 25.7283 21.3799L24.9168 20.5735C20.7512 16.9985 19.5206 12.3493 19.2077 7.70522C19.1934 7.49363 19.0185 7.32812 18.8064 7.32812H16.0395C15.8103 7.32812 15.6277 7.5204 15.6423 7.74917C15.985 13.1043 17.4621 18.4387 21.6518 22.3366C21.6889 22.3712 21.7206 22.4123 21.7391 22.4596C21.8514 22.7463 21.7665 22.9365 21.6381 23.0572C21.4795 23.2062 21.1757 23.1862 20.9493 23.0815C20.9167 23.0664 20.8875 23.0442 20.8617 23.0191C16.227 18.5143 14.8146 13.1601 14.5127 7.70966C14.5009 7.49623 14.3252 7.32812 14.1114 7.32812H12.1733C11.9464 7.32812 11.7646 7.51637 11.7757 7.74309C12.0422 13.1869 13.4571 19.4581 18.5771 24.8362C18.6268 24.8884 18.6642 24.9532 18.6745 25.0245C18.7033 25.2251 18.6667 25.3882 18.5058 25.5641C18.3878 25.6628 18.1107 25.7389 17.8623 25.6557C17.8158 25.6401 17.7758 25.6095 17.742 25.5739C12.3704 19.9156 10.9233 13.3228 10.6598 7.71227C10.6497 7.49748 10.4734 7.32812 10.2584 7.32812H8.4332Z" fill="#4C1E04" />
        </svg>;
      }
      break;
    case 'nobreach':
      return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 34 34" fill="none">
        <path d="M2.57568 5.66671L5.66659 2.57581H28.3333L31.4242 5.66671V28.3334L28.3333 31.4243H5.66659L2.57568 28.3334V5.66671Z" fill="#DEBEA6" />
        <path fillRule="evenodd" clipRule="evenodd" d="M5.23986 1.54541H28.7601L32.4545 5.23986V28.7601L28.7601 32.4545H5.23985L1.54541 28.7601V5.23986L5.23986 1.54541ZM6.09339 3.60602L3.60602 6.09339V27.9065L6.09338 30.3939H27.9065L30.3939 27.9065V6.09339L27.9065 3.60602H6.09339Z" fill="#F7D7BF" />
        <path fillRule="evenodd" clipRule="evenodd" d="M8.33936 25.8176V5.21155H24.8242V25.8176H19.6727V23.757H22.7636V7.27215H12.9757L17.6121 9.33276V29.9388L8.33936 25.8176ZM15.5515 16.2873C15.9782 16.2873 16.3242 16.6333 16.3242 17.06V21.1812C16.3242 21.608 15.9782 21.954 15.5515 21.954C15.1247 21.954 14.7787 21.608 14.7787 21.1812V17.06C14.7787 16.6333 15.1247 16.2873 15.5515 16.2873Z" fill="#412A1C" />
      </svg>;
    case 'breach_attach':
      return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 34 34" fill="none">
        <path d="M2.57568 5.66671L5.66659 2.57581H28.3333L31.4242 5.66671V28.3334L28.3333 31.4243H5.66659L2.57568 28.3334V5.66671Z" fill="#DEBEA6" />
        <path fillRule="evenodd" clipRule="evenodd" d="M5.23986 1.54541H28.7601L32.4545 5.23986V28.7601L28.7601 32.4545H5.23985L1.54541 28.7601V5.23986L5.23986 1.54541ZM6.09339 3.60602L3.60602 6.09339V27.9065L6.09338 30.3939H27.9065L30.3939 27.9065V6.09339L27.9065 3.60602H6.09339Z" fill="#F7D7BF" />
        <path d="M20.7429 15.2282C21.0446 14.9264 21.0446 14.4372 20.7429 14.1354C20.4411 13.8336 19.9518 13.8336 19.6501 14.1354L16.5592 17.2263C16.2574 17.5281 16.2574 18.0174 16.5592 18.3191C16.8609 18.6209 17.3502 18.6209 17.652 18.3191L20.7429 15.2282Z" fill="#412A1C" />
        <path fillRule="evenodd" clipRule="evenodd" d="M21.2268 4.12122C21.6535 4.12122 21.9995 4.46718 21.9995 4.89394V7.4697H23.5677L25.348 9.24998V26.2955L23.5677 28.0758H10.6434L8.86313 26.2955V9.24998L10.6434 7.4697H20.454V4.89394C20.454 4.46718 20.8 4.12122 21.2268 4.12122ZM17.1056 23.9545C20.5197 23.9545 23.2874 21.1869 23.2874 17.7727C23.2874 14.3586 20.5197 11.5909 17.1056 11.5909C13.6914 11.5909 10.9237 14.3586 10.9237 17.7727C10.9237 21.1869 13.6914 23.9545 17.1056 23.9545Z" fill="#412A1C" />
        <path d="M7.83282 11.5909H5.77222V14.6818H7.83282V11.5909Z" fill="#412A1C" />
        <path d="M7.83282 21.8939H5.77222V24.9849H7.83282V21.8939Z" fill="#412A1C" />
        <path d="M26.3783 11.5909H28.4389V14.6818H26.3783V11.5909Z" fill="#412A1C" />
        <path d="M26.3783 21.8939H28.4389V24.9849H26.3783V21.8939Z" fill="#412A1C" />
        <path d="M26.3783 16.7424H28.4389V19.8333H26.3783V16.7424Z" fill="#412A1C" />
        <path d="M5.77222 16.7424H7.83282V19.8333H5.77222V16.7424Z" fill="#412A1C" />
      </svg>;
    case 'breach_throw':
      return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 34 34" fill="none">
        <path d="M2.57568 5.66671L5.66659 2.57581H28.3333L31.4242 5.66671V28.3334L28.3333 31.4243H5.66659L2.57568 28.3334V5.66671Z" fill="#DEBEA6" />
        <path fillRule="evenodd" clipRule="evenodd" d="M5.23986 1.54541H28.7601L32.4545 5.23986V28.7601L28.7601 32.4545H5.23985L1.54541 28.7601V5.23986L5.23986 1.54541ZM6.09339 3.60602L3.60602 6.09339V27.9065L6.09338 30.3939H27.9065L30.3939 27.9065V6.09339L27.9065 3.60602H6.09339Z" fill="#F7D7BF" />
        <path fillRule="evenodd" clipRule="evenodd" d="M16.644 7.2861L19.5968 4.23218L29.4433 9.91706L22.0178 24.9681L19.3312 25.0965L26.4624 10.8133L23.8171 9.28608L22.8898 10.8922L24.6744 11.9225L23.1289 14.5993L22.2367 14.0841L15.0245 26.5759L15.9168 27.091L14.3714 29.7678L4.5564 24.1012L6.10185 21.4244L6.99412 21.9395L14.2062 9.44775L13.314 8.9326L14.8594 6.2558L16.644 7.2861ZM16.6509 13.5634C16.2528 14.2529 15.3711 14.4891 14.6816 14.091C13.9921 13.693 13.7559 12.8113 14.154 12.1218C14.5521 11.4323 15.4337 11.1961 16.1232 11.5942C16.8127 11.9922 17.0489 12.8739 16.6509 13.5634ZM19.98 15.4855C19.5819 16.175 18.7003 16.4112 18.0108 16.0131C17.3213 15.615 17.0851 14.7334 17.4831 14.0439C17.8812 13.3544 18.7629 13.1182 19.4524 13.5163C20.1418 13.9143 20.3781 14.796 19.98 15.4855ZM17.5774 19.6469C17.1793 20.3364 16.2977 20.5726 15.6082 20.1746C14.9187 19.7765 14.6825 18.8948 15.0805 18.2053C15.4786 17.5159 16.3603 17.2796 17.0497 17.6777C17.7392 18.0758 17.9755 18.9574 17.5774 19.6469ZM14.2482 17.7248C13.8502 18.4143 12.9685 18.6506 12.279 18.2525C11.5895 17.8544 11.3533 16.9728 11.7514 16.2833C12.1495 15.5938 13.0311 15.3575 13.7206 15.7556C14.4101 16.1537 14.6463 17.0353 14.2482 17.7248ZM11.8456 21.8863C11.4476 22.5758 10.5659 22.812 9.87642 22.4139C9.18693 22.0158 8.95069 21.1342 9.34877 20.4447C9.74684 19.7552 10.6285 19.519 11.318 19.9171C12.0075 20.3151 12.2437 21.1968 11.8456 21.8863ZM15.1748 23.8084C14.7767 24.4978 13.8951 24.7341 13.2056 24.336C12.5161 23.9379 12.2798 23.0563 12.6779 22.3668C13.076 21.6773 13.9576 21.4411 14.6471 21.8391C15.3366 22.2372 15.5729 23.1189 15.1748 23.8084Z" fill="#412A1C" />
      </svg>;
    case 'pair':
      return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 33 33" fill="none">
        <path d="M31 16.5C31 24.5081 24.5081 31 16.5 31C8.49187 31 2 24.5081 2 16.5C2 8.49187 8.49187 2 16.5 2C24.5081 2 31 8.49187 31 16.5Z" fill="#E69800" />
        <path fillRule="evenodd" clipRule="evenodd" d="M16.5 3C9.04416 3 3 9.04416 3 16.5C3 23.9558 9.04416 30 16.5 30C23.9558 30 30 23.9558 30 16.5C30 9.04416 23.9558 3 16.5 3ZM1 16.5C1 7.93959 7.93959 1 16.5 1C25.0604 1 32 7.93959 32 16.5C32 25.0604 25.0604 32 16.5 32C7.93959 32 1 25.0604 1 16.5Z" fill="#FAA500" />
        <path d="M11.8882 4.84375C10.0938 4.84375 8.6396 6.30031 8.6396 8.09649C8.6396 9.29271 9.28451 10.3379 10.2448 10.9028L8.45308 12.113C8.43806 12.1231 8.4245 12.1353 8.41275 12.1491C8.11707 12.4963 7.98194 12.9884 7.91609 13.4834C7.84995 13.9804 7.84996 14.5144 7.84996 14.9698V14.976C7.84996 15.0473 7.88787 15.1131 7.94947 15.1489C7.9671 15.1592 7.99622 15.1735 8.01595 15.1832L8.0203 15.1854L8.04965 15.1999L8.05984 15.2051L8.06342 15.207C9.18897 15.8458 10.4955 16.2112 11.8882 16.2112C13.2078 16.2112 14.4501 15.8832 15.5344 15.3053C15.5591 15.2921 15.5888 15.2767 15.6213 15.2598L15.6243 15.2582C15.6907 15.2237 15.7698 15.1825 15.8277 15.1489C15.8893 15.1131 15.9272 15.0473 15.9272 14.976V14.9698C15.9272 14.5143 15.9272 13.9804 15.861 13.4833C15.795 12.9883 15.6597 12.4962 15.3635 12.149C15.3518 12.1352 15.3383 12.1231 15.3233 12.113L13.5316 10.9028C14.4919 10.3379 15.1368 9.29271 15.1368 8.09649C15.1368 6.30031 13.6826 4.84375 11.8882 4.84375Z" fill="#4C1E04" />
        <path fillRule="evenodd" clipRule="evenodd" d="M21.1133 4.85232C19.2395 4.85232 17.7218 6.37317 17.7218 8.24768C17.7218 9.30664 18.2062 10.2521 18.9642 10.8744L17.6986 11.7292C17.6587 11.7562 17.6225 11.7884 17.5913 11.8252C17.2483 12.228 17.1105 12.7713 17.0462 13.2546C16.9812 13.7434 16.9812 14.263 16.9812 14.6844V14.7C16.9812 14.8896 17.0821 15.0649 17.246 15.1601C17.2658 15.1716 17.2859 15.1818 17.2953 15.1866L17.2966 15.1872L17.3304 15.204L17.3565 15.2169L17.3585 15.2179L17.3613 15.2194C18.4665 15.8455 19.7484 16.2031 21.1133 16.2031C22.4086 16.2031 23.6293 15.881 24.6951 15.3129C24.7117 15.3041 24.7353 15.2918 24.7625 15.2776L24.7654 15.2761C24.8307 15.2422 24.9199 15.1958 24.9813 15.1601C25.1452 15.0649 25.2461 14.8896 25.2461 14.7V14.6843C25.2461 14.263 25.2461 13.7433 25.181 13.2544C25.1165 12.7711 24.9786 12.2277 24.635 11.8249C24.6038 11.7883 24.5678 11.7561 24.528 11.7292L23.2624 10.8744C24.0204 10.2521 24.5048 9.30664 24.5048 8.24768C24.5048 6.37317 22.987 4.85232 21.1133 4.85232ZM19.5105 8.26992C19.5105 7.38543 20.2261 6.67113 21.1055 6.67113C21.9848 6.67113 22.7004 7.38543 22.7004 8.26992C22.7004 8.91819 22.3157 9.47749 21.7598 9.72873C21.4496 9.86891 21.325 9.91054 21.0913 9.91054C20.8577 9.91054 20.7613 9.86891 20.4511 9.72873C19.8952 9.47749 19.5105 8.91819 19.5105 8.26992ZM20.8335 11.7442C20.9428 11.6769 21.0248 11.6264 21.1055 11.5457C21.1749 11.6151 21.2617 11.6697 21.3768 11.7422C21.4358 11.7793 21.5022 11.8212 21.5777 11.8721L23.2879 13.0273C23.3187 13.107 23.3563 13.2416 23.3843 13.4521C23.4028 13.5908 23.4145 13.7391 23.4218 13.8962C22.7161 14.2048 21.933 14.377 21.1055 14.377C20.2783 14.377 19.4954 14.2049 18.7899 13.8965C18.7971 13.7392 18.8088 13.5908 18.8273 13.4519C18.8553 13.2415 18.8928 13.1068 18.9235 13.0269L20.6333 11.8721C20.7102 11.8201 20.7757 11.7798 20.8335 11.7442Z" fill="#4C1E04" />
        <path d="M21.2075 16.7305C23.0019 16.7305 24.4561 18.187 24.4561 19.9832C24.4561 21.1794 23.8112 22.2246 22.851 22.7896L24.6426 23.9997C24.6576 24.0099 24.6712 24.022 24.683 24.0358C24.9786 24.383 25.1138 24.8752 25.1796 25.3701C25.2457 25.8671 25.2457 26.4011 25.2457 26.8565V26.8627C25.2457 26.934 25.2078 26.9998 25.1462 27.0356C25.1286 27.0459 25.0995 27.0602 25.0798 27.0699L25.0754 27.0721L25.0461 27.0866L25.0359 27.0918L25.0323 27.0937C23.9067 27.7325 22.6002 28.098 21.2075 28.098C19.8879 28.098 18.6456 27.7699 17.5613 27.192C17.5366 27.1788 17.5069 27.1634 17.4744 27.1465L17.4714 27.1449C17.405 27.1104 17.3259 27.0693 17.268 27.0356C17.2064 26.9998 17.1685 26.934 17.1685 26.8627V26.8565C17.1685 26.4011 17.1685 25.8671 17.2347 25.37C17.3007 24.8751 17.436 24.3829 17.7322 24.0357C17.7439 24.0219 17.7574 24.0098 17.7724 23.9997L19.5641 22.7896C18.6038 22.2246 17.9589 21.1794 17.9589 19.9832C17.9589 18.187 19.4131 16.7305 21.2075 16.7305Z" fill="#4C1E04" />
        <path fillRule="evenodd" clipRule="evenodd" d="M11.9824 16.739C13.8562 16.739 15.3739 18.2599 15.3739 20.1344C15.3739 21.1934 14.8895 22.1388 14.1315 22.7611L15.3971 23.6159C15.4371 23.6429 15.4732 23.6752 15.5044 23.7119C15.8474 24.1147 15.9852 24.658 16.0495 25.1413C16.1145 25.6301 16.1145 26.1498 16.1145 26.5711V26.5867C16.1145 26.7763 16.0136 26.9516 15.8497 27.0468C15.8299 27.0583 15.8098 27.0686 15.8004 27.0733L15.7991 27.0739L15.7653 27.0907L15.7392 27.1036L15.7372 27.1047L15.7344 27.1061C14.6292 27.7322 13.3473 28.0898 11.9824 28.0898C10.6871 28.0898 9.46639 27.7677 8.40059 27.1996C8.384 27.1908 8.36045 27.1785 8.33321 27.1644L8.33034 27.1629C8.26499 27.1289 8.17581 27.0825 8.11442 27.0468C7.95049 26.9516 7.84961 26.7763 7.84961 26.5867V26.5711C7.8496 26.1497 7.8496 25.6301 7.91475 25.1412C7.97917 24.6578 8.11713 24.1144 8.46069 23.7116C8.49189 23.675 8.52791 23.6428 8.56775 23.6159L9.83332 22.7611C9.07534 22.1388 8.59095 21.1934 8.59095 20.1344C8.59095 18.2599 10.1087 16.739 11.9824 16.739ZM13.5852 20.1566C13.5852 19.2722 12.8696 18.5578 11.9902 18.5578C11.1109 18.5578 10.3953 19.2722 10.3953 20.1566C10.3953 20.8049 10.78 21.3642 11.3359 21.6154C11.6461 21.7556 11.7707 21.7973 12.0044 21.7973C12.238 21.7973 12.3344 21.7556 12.6446 21.6154C13.2005 21.3642 13.5852 20.8049 13.5852 20.1566ZM12.2622 23.6309C12.1529 23.5636 12.0709 23.5131 11.9902 23.4324C11.9208 23.5019 11.834 23.5564 11.7189 23.6289C11.66 23.666 11.5933 23.708 11.5181 23.7588L9.80776 24.914C9.777 24.9937 9.73941 25.1284 9.71137 25.3388C9.69288 25.4775 9.6812 25.6258 9.67393 25.7829C10.3796 26.0915 11.1627 26.2637 11.9902 26.2637C12.8174 26.2637 13.6003 26.0916 14.3058 25.7832C14.2986 25.626 14.2869 25.4775 14.2684 25.3386C14.2404 25.1282 14.2029 24.9935 14.1722 24.9137L12.4624 23.7588C12.3855 23.7069 12.32 23.6665 12.2622 23.6309Z" fill="#4C1E04" />
      </svg>;
    case 'control':
      return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 33 33" fill="none">
        <path d="M31 16.5C31 24.5081 24.5081 31 16.5 31C8.49187 31 2 24.5081 2 16.5C2 8.49187 8.49187 2 16.5 2C24.5081 2 31 8.49187 31 16.5Z" fill="#E69800" />
        <path fillRule="evenodd" clipRule="evenodd" d="M16.5 3C9.04416 3 3 9.04416 3 16.5C3 23.9558 9.04416 30 16.5 30C23.9558 30 30 23.9558 30 16.5C30 9.04416 23.9558 3 16.5 3ZM1 16.5C1 7.93959 7.93959 1 16.5 1C25.0604 1 32 7.93959 32 16.5C32 25.0604 25.0604 32 16.5 32C7.93959 32 1 25.0604 1 16.5Z" fill="#FAA500" />
        <path d="M13.9755 9.99821C14.4293 9.79019 14.3736 9.12835 13.8914 8.99915L8.10103 7.44762C7.70482 7.34146 7.34227 7.70401 7.44843 8.10022L8.99983 13.8901C9.12904 14.3723 9.79099 14.428 9.99893 13.9741L10.3861 13.129C10.5262 12.8232 10.9111 12.7221 11.1834 12.9196L14.794 15.5387C15.2848 15.8947 15.8942 15.2853 15.5381 14.7945L12.919 11.1839C12.7215 10.9116 12.8225 10.5268 13.1283 10.3866L13.9755 9.99821Z" fill="#4C1E04" />
        <path d="M23.0028 19.0254C23.2108 18.5717 23.8726 18.6274 24.0018 19.1096L25.5534 24.9C25.6595 25.2962 25.297 25.6587 24.9008 25.5525L19.1109 24.0011C18.6286 23.8719 18.573 23.21 19.0268 23.002L19.872 22.6149C20.1778 22.4747 20.2789 22.0898 20.0814 21.8176L17.4623 18.207C17.1063 17.7162 17.7157 17.1068 18.2065 17.4628L21.8171 20.082C22.0894 20.2795 22.4742 20.1784 22.6144 19.8727L23.0028 19.0254Z" fill="#4C1E04" />
        <path d="M13.9755 23.0018C14.4293 23.2098 14.3736 23.8716 13.8914 24.0008L8.10102 25.5524C7.70482 25.6585 7.34227 25.296 7.44843 24.8998L8.99983 19.1099C9.12904 18.6277 9.79099 18.572 9.99893 19.0259L10.3861 19.871C10.5262 20.1768 10.9111 20.2779 11.1834 20.0804L14.794 17.4613C15.2848 17.1053 15.8942 17.7147 15.5381 18.2055L12.919 21.8161C12.7215 22.0884 12.8225 22.4732 13.1283 22.6134L13.9755 23.0018Z" fill="#4C1E04" />
        <path d="M23.0028 13.9746C23.2108 14.4283 23.8726 14.3726 24.0018 13.8904L25.5534 8.10005C25.6595 7.70385 25.297 7.34129 24.9008 7.44746L19.1109 8.99885C18.6286 9.12807 18.573 9.79002 19.0268 9.99796L19.872 10.3851C20.1778 10.5253 20.2789 10.9102 20.0814 11.1824L17.4623 14.793C17.1063 15.2838 17.7157 15.8932 18.2065 15.5372L21.8171 12.918C22.0894 12.7205 22.4742 12.8216 22.6144 13.1273L23.0028 13.9746Z" fill="#4C1E04" />
      </svg>;
    case 'supress':
      return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 33 33" fill="none">
        <path d="M31 16.5C31 24.5081 24.5081 31 16.5 31C8.49187 31 2 24.5081 2 16.5C2 8.49187 8.49187 2 16.5 2C24.5081 2 31 8.49187 31 16.5Z" fill="#E69800" />
        <path fillRule="evenodd" clipRule="evenodd" d="M16.5 3C9.04416 3 3 9.04416 3 16.5C3 23.9558 9.04416 30 16.5 30C23.9558 30 30 23.9558 30 16.5C30 9.04416 23.9558 3 16.5 3ZM1 16.5C1 7.93959 7.93959 1 16.5 1C25.0604 1 32 7.93959 32 16.5C32 25.0604 25.0604 32 16.5 32C7.93959 32 1 25.0604 1 16.5Z" fill="#FAA500" />
        <path fillRule="evenodd" clipRule="evenodd" d="M16.3387 15.5484C15.0684 15.5484 14.0387 16.5782 14.0387 17.8484C14.0387 19.1187 15.0684 20.1484 16.3387 20.1484C17.6089 20.1484 18.6387 19.1187 18.6387 17.8484C18.6387 16.5782 17.6089 15.5484 16.3387 15.5484ZM15.1387 17.8484C15.1387 17.1857 15.6759 16.6484 16.3387 16.6484C17.0014 16.6484 17.5387 17.1857 17.5387 17.8484C17.5387 18.5112 17.0014 19.0484 16.3387 19.0484C15.6759 19.0484 15.1387 18.5112 15.1387 17.8484Z" fill="#4C1E04" />
        <path fillRule="evenodd" clipRule="evenodd" d="M8.67773 6.89147C14.4667 5.8637 17.7649 5.71128 23.9992 6.89147V27.0009H21.1264V25.0857H11.5505V27.0009H8.67773V6.89147ZM11.6191 9.12653H21.0566V10.6544H11.6191V9.12653ZM15.1387 12.1484H17.5387V13.8224C18.8935 14.2257 19.9615 15.2936 20.3647 16.6484H22.0387V19.0484H20.3647C19.9615 20.4032 18.8935 21.4712 17.5387 21.8745V23.5484H15.1387V21.8745C13.7839 21.4712 12.7159 20.4032 12.3127 19.0484L10.6387 19.0484V16.6484L12.3127 16.6484C12.7159 15.2936 13.7839 14.2257 15.1387 13.8224V12.1484Z" fill="#4C1E04" />
      </svg>;
    default:
      break;
  }
  return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 34 34" fill="none"></svg>;
}
